<template>
  <v-row>
    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      :timeout="snackbarTimeout"
      :top="true"
    >
      {{ snackbarMsg }}
      <v-btn dark text @click="showSnackbar = false">CLOSE</v-btn>
    </v-snackbar>

    <!-- Tonnage Selection Dialog -->
    <dialog-tonnage-selection
      :dialog="dialogTonnageSelection"
      :actual-tonnage-rate="actualTonnageRate"
      :tonnage-rates="tonnageRates"
      :transaction="transaction_type"
      v-on:tonnage-selected="tonnageSelected"
      v-on:dialog-close="onDialogClose"
    ></dialog-tonnage-selection>

    <!-- Converted Currency list dialog -->
    <v-dialog
      v-model="dialogOnConvertedCurrencies"
      max-width="230"
      v-if="quotation != null"
    >
      <v-row no-gutters>
        <v-col>
          <v-card color="white" flat v-if="selectedFCItem != null">
            <v-card-title class="headline primary--text">
              <v-row justify="center">
                <span>Convert to {{ quotation.currency }}</span>
              </v-row>
            </v-card-title>

            <v-card-text>
              <v-row no-gutters>
                <v-col class="d-flex justify-content-start">
                  <span class="font-weight-bold body-2">Currency</span>
                </v-col>
                <v-col class="d-flex justify-content-end">
                  <span class="font-weight-bold body-2">Rate</span>
                </v-col>
              </v-row>
              <v-divider></v-divider>

              <template v-for="(item, i) in convertedCurrenciesList">
                <v-row
                  class="update-converted-currency py-2 px-2"
                  :key="i"
                  style="cursor: pointer"
                  @click="updateFCItemConverted(item)"
                  no-gutters
                >
                  <v-col class="d-flex justify-content-start">
                    <span class="font-weight-bold caption">{{ item.base_currency }}</span>
                  </v-col>

                  <v-col class="d-flex justify-content-end">
                    <span class="font-weight-bold caption">{{
                      item.conversion_rate.toFixed(4)
                    }}</span>
                  </v-col>
                </v-row>
                <v-divider class="py-0 my-0" :key="`${i}-d`"></v-divider>
              </template>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-dialog>

    <v-row dense>
      <v-col v-if="fc != null">
        <v-card flat tile>
          <v-row no-gutters>
            <v-col>
              <table class="financial-table">
                <!-- Header -->
                <tr>
                  <th class="action-cell"></th>
                  <th colspan="2">Description</th>
                  <th :style="getQuantityColumnWidth()">Qty</th>
                  <th :style="getNumericalColumnWidth()">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <span v-on="on">B. PPU</span>
                      </template>
                      Price per unit in original currency
                    </v-tooltip>
                  </th>
                  <th :style="getNumericalColumnWidth()">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <span v-on="on">C. PPU ({{ quotation.currency }})</span>
                      </template>
                      Price per unit after conversion to
                      {{ quotation.currency }}
                    </v-tooltip>
                  </th>
                  <th :style="getNumericalColumnWidth()">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <span v-on="on">Total ({{ quotation.currency }})</span>
                      </template>
                      Total price after conversion to {{ quotation.currency }}
                    </v-tooltip>
                  </th>
                  <th :style="getNumericalColumnWidth()" v-if="show_cost_component">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <span v-on="on">B. CPU</span>
                      </template>
                      Cost per unit in original currency
                    </v-tooltip>
                  </th>
                  <th :style="getNumericalColumnWidth()" v-if="show_cost_component">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <span v-on="on">C. CPU ({{ quotation.currency }})</span>
                      </template>
                      Cost per unit after conversion to {{ quotation.currency }}
                    </v-tooltip>
                  </th>
                  <th :style="getNumericalColumnWidth()" v-if="show_cost_component">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <span v-on="on">Total ({{ quotation.currency }})</span>
                      </template>
                      Total cost after conversion to {{ quotation.currency }}
                    </v-tooltip>
                  </th>
                  <th :style="getNumericalColumnWidth()" v-if="show_cost_component">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <span v-on="on">Margin</span>
                      </template>
                      Expected margin in %
                    </v-tooltip>
                  </th>
                </tr>

                <!-- Ocean (Sea) Freight charges -->
                <tbody
                  class="line-item-set"
                  v-if="
                    fc.hasOwnProperty(CONSTANT.TYPE_OCEAN_FREIGHT_CHARGES) &&
                    show_ocean_freight_charges &&
                    (total_price_cost_ocean_freight_charges.price > 0 ||
                      total_price_cost_ocean_freight_charges.total_qty > 0 ||
                      showCost)
                  "
                >
                  <tr class="line-item-set-header grey lighten-4">
                    <td class="component-title-header">
                      <v-btn
                        x-small
                        icon
                        @click="
                          showOceanFreightChargesItems = !showOceanFreightChargesItems
                        "
                        style="background-color: gainsboro"
                      >
                        <v-icon>{{
                          !showOceanFreightChargesItems ? "add" : "remove"
                        }}</v-icon>
                      </v-btn>
                    </td>
                    <td
                      class="single-description description font-weight-bold"
                      colspan="2"
                    >
                      Freight charges
                      {{
                        quotation.origin_port.un_code +
                        "-" +
                        quotation.destination_port.un_code
                      }}
                    </td>
                    <!-- <td></td> -->
                    <td class="input-cell" colspan="8"></td>
                    <!-- <td class="input-cell font-weight-bold">
                      <input
                        class="input-number"
                        :placeholder="total_price_cost_ocean_freight_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })"
                        :value="total_price_cost_ocean_freight_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })"
                        readonly
                      />
                    </td>
                    <td
                      class="input-cell"
                      v-if="show_cost_component"
                      colspan="2"
                    ></td>
                    <td
                      class="input-cell font-weight-bold"
                      v-if="show_cost_component"
                    >
                      <input
                        class="input-number"
                        :value="
                          total_price_cost_ocean_freight_charges.cost.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        readonly
                      />
                    </td>
                    <td
                      class="margin-cell"
                      v-if="show_cost_component"
                    >
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold"
                        :value="
                          getMargin(
                            total_price_cost_ocean_freight_charges.price,
                            total_price_cost_ocean_freight_charges.cost
                          )
                        "
                        readonly
                      />
                    </td> -->
                  </tr>

                  <template v-for="(item, i) in fc[CONSTANT.TYPE_OCEAN_FREIGHT_CHARGES]">
                    <tr
                      class="line-item-set-items"
                      v-show="
                        showSubLineItem(showOceanFreightChargesItems, item) &&
                        (item.price.converted > 0.0 || $root.is_admin)
                      "
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        <div v-if="$root.is_admin && !quotation.has_freight_rate">
                          <input
                            type="text"
                            class="input-description"
                            @input="handleInput"
                            :placeholder="item.description"
                            :readonly="!show_cost_component"
                            v-model="item.description"
                          />
                        </div>
                        <div v-else>
                          {{ item.description + " " + getCurrencyUnitPriceText(item) }}
                        </div>
                      </td>
                      <td>
                        <v-chip
                          x-small
                          label
                          outlined
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                          >{{ item.currency }}</v-chip
                        >
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput()"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :readonly="!show_cost_component"
                          :placeholder="item.price.base.toFixed(2)"
                          v-model.number="item.price.base"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          v-if="!CONSTANT.isObjectEmpty(item)"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          class="input-number input-number-total"
                          :placeholder="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.cost.base"
                          @input="handleBaseRateInput(item.cost, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          v-if="!CONSTANT.isObjectEmpty(item)"
                          :value="item.cost.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          class="input-number input-number-total"
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.cost.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="margin-cell" v-if="show_cost_component">
                        <input
                          type="text"
                          class="input-number input-number-margin"
                          :value="getMargin(item.price.converted, item.cost.converted)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>

                  <tr class="line-item-set-header blue-lighten-6">
                    <td class="input-cell" colspan="6"></td>
                    <td class="input-cell font-weight-bold">
                      <input
                        class="input-number blue-lighten-6 font-weight-bold"
                        :placeholder="
                          total_price_cost_ocean_freight_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        :value="
                          total_price_cost_ocean_freight_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        readonly
                      />
                    </td>
                    <td class="input-cell" v-if="show_cost_component" colspan="2"></td>
                    <td class="input-cell font-weight-bold" v-if="show_cost_component">
                      <input
                        class="input-number blue-lighten-6 font-weight-bold"
                        :value="
                          total_price_cost_ocean_freight_charges.cost.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        readonly
                      />
                    </td>
                    <td class="margin-cell" v-if="show_cost_component">
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold blue-lighten-6"
                        :value="
                          getMargin(
                            total_price_cost_ocean_freight_charges.price,
                            total_price_cost_ocean_freight_charges.cost
                          )
                        "
                        readonly
                      />
                    </td>
                  </tr>
                </tbody>

                <!-- Air Freight charges -->
                <tbody
                  class="line-item-set"
                  v-if="
                    fc.hasOwnProperty(CONSTANT.TYPE_AIR_FREIGHT_CHARGES) &&
                    show_air_freight_charges &&
                    (total_price_cost_air_freight_charges.price > 0 ||
                      total_price_cost_air_freight_charges.total_qty > 0 ||
                      showCost)
                  "
                >
                  <tr class="line-item-set-header grey lighten-4">
                    <td class="component-title-header">
                      <v-btn
                        x-small
                        icon
                        @click="showAirFreightChargesItems = !showAirFreightChargesItems"
                        style="background-color: gainsboro"
                      >
                        <v-icon>{{
                          !showAirFreightChargesItems ? "add" : "remove"
                        }}</v-icon>
                      </v-btn>
                    </td>
                    <td
                      class="single-description description font-weight-bold"
                      colspan="2"
                    >
                      Freight charges
                      {{
                        quotation.origin_port.un_code +
                        "-" +
                        quotation.destination_port.un_code
                      }}
                    </td>
                    <!-- <td></td> -->
                    <td class="input-cell" colspan="8"></td>
                    <!-- <td class="input-cell font-weight-bold">
                      <input
                        class="input-number"
                        :placeholder="total_price_cost_air_freight_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        :value="
                          total_price_cost_air_freight_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        readonly
                      />
                    </td>
                    <td
                      class="input-cell"
                      v-if="show_cost_component"
                      colspan="2"
                    ></td>
                    <td
                      class="input-cell font-weight-bold"
                      v-if="show_cost_component"
                    >
                      <input
                        class="input-number"
                        :value="
                          total_price_cost_air_freight_charges.cost.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        readonly
                      />
                    </td>
                    <td
                      class="margin-cell"
                      v-if="show_cost_component"
                    >
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold"
                        :value="
                          getMargin(
                            total_price_cost_air_freight_charges.price,
                            total_price_cost_air_freight_charges.cost
                          )
                        "
                        readonly
                      />
                    </td> -->
                  </tr>

                  <template v-for="(item, i) in fc[CONSTANT.TYPE_AIR_FREIGHT_CHARGES]">
                    <tr
                      class="line-item-set-items"
                      v-show="
                        showSubLineItem(showAirFreightChargesItems, item) &&
                        (item.price.converted > 0.0 || $root.is_admin)
                      "
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        <div v-if="$root.is_admin && !quotation.has_freight_rate">
                          <input
                            type="text"
                            class="input-description"
                            @input="handleInput"
                            :placeholder="item.description"
                            :readonly="!show_cost_component"
                            v-model="item.description"
                          />
                        </div>
                        <div v-else>
                          {{ item.description + " " + getCurrencyUnitPriceText(item) }}
                        </div>
                      </td>
                      <td>
                        <v-chip
                          x-small
                          label
                          outlined
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                          >{{ item.currency }}</v-chip
                        >
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput()"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :readonly="!show_cost_component"
                          :placeholder="item.price.base"
                          v-model.number="item.price.base"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          v-if="!CONSTANT.isObjectEmpty(item)"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          class="input-number input-number-total"
                          :placeholder="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.cost.base"
                          @input="handleBaseRateInput(item.cost, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          v-if="!CONSTANT.isObjectEmpty(item)"
                          :value="item.cost.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          class="input-number input-number-total"
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.cost.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="margin-cell" v-if="show_cost_component">
                        <input
                          type="text"
                          class="input-number input-number-margin"
                          :value="getMargin(item.price.converted, item.cost.converted)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>

                  <tr class="line-item-set-header blue-lighten-6">
                    <td class="input-cell" colspan="6"></td>
                    <td class="input-cell">
                      <input
                        class="input-number font-weight-bold blue-lighten-6"
                        :placeholder="
                          total_price_cost_air_freight_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        :value="
                          total_price_cost_air_freight_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        readonly
                      />
                    </td>
                    <td class="input-cell" v-if="show_cost_component" colspan="2"></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number font-weight-bold blue-lighten-6"
                        :value="
                          total_price_cost_air_freight_charges.cost.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        readonly
                      />
                    </td>
                    <td class="margin-cell" v-if="show_cost_component">
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold blue-lighten-6"
                        :value="
                          getMargin(
                            total_price_cost_air_freight_charges.price,
                            total_price_cost_air_freight_charges.cost
                          )
                        "
                        readonly
                      />
                    </td>
                  </tr>
                </tbody>

                <!-- Insurance -->
                <tbody
                  class="line-item-set"
                  v-if="
                    fc.hasOwnProperty(CONSTANT.TYPE_INSURANCE_CHARGES) &&
                    show_insurance_charges &&
                    (total_price_cost_insurance_charges.price > 0 ||
                      total_price_cost_insurance_charges.total_qty > 0)
                  "
                >
                  <tr class="line-item-set-header grey lighten-4">
                    <td class="component-title-header">
                      <v-btn
                        x-small
                        icon
                        @click="showInsuranceItems = !showInsuranceItems"
                        style="background-color: gainsboro"
                      >
                        <v-icon>{{ !showInsuranceItems ? "add" : "remove" }}</v-icon>
                      </v-btn>
                    </td>
                    <td
                      class="single-description description font-weight-bold"
                      colspan="2"
                    >
                      Insurance
                    </td>

                    <!-- <td></td> -->

                    <td class="input-cell" colspan="8"></td>
                    <!-- <td class="input-cell">
                      <input
                        class="input-number font-weight-bold"
                        :placeholder="
                          total_price_cost_insurance_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        :value="
                          total_price_cost_insurance_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        readonly
                      />
                    </td>
                    <td
                      class="input-cell"
                      v-if="show_cost_component"
                      colspan="2"
                    ></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number font-weight-bold"
                        :value="
                          total_price_cost_insurance_charges.cost.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        readonly
                        v-if="show_cost_component"
                      />
                    </td>
                    <td
                      class="margin-cell"
                      v-if="show_cost_component"
                    >
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold"
                        :value="
                          getMargin(
                            total_price_cost_insurance_charges.price,
                            total_price_cost_insurance_charges.cost
                          )
                        "
                        readonly
                      />
                    </td> -->
                  </tr>

                  <template v-for="(item, i) in fc[CONSTANT.TYPE_INSURANCE_CHARGES]">
                    <tr
                      class="line-item-set-items"
                      v-show="showSubLineItem(showInsuranceItems, item)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        {{ item.description + " " + getCurrencyUnitPriceText(item) }}
                      </td>
                      <td>
                        <v-chip
                          x-small
                          label
                          outlined
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                          >{{ item.currency }}</v-chip
                        >
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.base"
                          v-model.number="item.price.base"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          class="input-number"
                          :placeholder="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.cost.base"
                          @input="handleBaseRateInput(item.cost, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          :value="item.cost.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          class="input-number"
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.cost.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="margin-cell" v-if="show_cost_component">
                        <input
                          type="text"
                          class="input-number input-number-margin"
                          :value="getMargin(item.price.converted, item.cost.converted)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>

                  <tr class="line-item-set-header blue-lighten-6">
                    <td class="input-cell" colspan="6"></td>
                    <td class="input-cell">
                      <input
                        class="input-number blue-lighten-6 font-weight-bold"
                        :placeholder="
                          total_price_cost_insurance_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        :value="
                          total_price_cost_insurance_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        readonly
                      />
                    </td>
                    <td class="input-cell" v-if="show_cost_component" colspan="2"></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number blue-lighten-6 font-weight-bold"
                        :value="
                          total_price_cost_insurance_charges.cost.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        readonly
                        v-if="show_cost_component"
                      />
                    </td>
                    <td class="margin-cell" v-if="show_cost_component">
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold blue-lighten-6"
                        :value="
                          getMargin(
                            total_price_cost_insurance_charges.price,
                            total_price_cost_insurance_charges.cost
                          )
                        "
                        readonly
                      />
                    </td>
                  </tr>
                </tbody>

                <!-- Export Cargo type charges -->
                <tbody
                  class="line-item-set"
                  v-if="
                    fc.hasOwnProperty(CONSTANT.TYPE_EXPORT_DGCLASS_CHARGES) &&
                    show_export_dgclass_charges &&
                    (total_price_cost_export_dgclass_charges.price > 0 ||
                      total_price_cost_export_dgclass_charges.total_qty > 0)
                  "
                >
                  <tr class="line-item-set-header grey lighten-4">
                    <td class="component-title-header">
                      <v-btn
                        x-small
                        icon
                        @click="
                          showExportCargoChargesTypeItems = !showExportCargoChargesTypeItems
                        "
                        style="background-color: gainsboro"
                      >
                        <v-icon>{{
                          !showExportCargoChargesTypeItems ? "add" : "remove"
                        }}</v-icon>
                      </v-btn>
                    </td>
                    <td
                      class="single-description description font-weight-bold"
                      colspan="2"
                    >
                      Export cargo specific charges at
                      {{ quotation.origin_port.name }}
                    </td>
                    <!-- <td></td> -->
                    <td class="input-cell" colspan="8"></td>
                    <!-- <td class="input-cell">
                      <input
                        class="input-number font-weight-bold"
                        :placeholder="
                          total_price_cost_export_dgclass_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        :value="
                          total_price_cost_export_dgclass_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        readonly
                      />
                    </td>
                    <td
                      class="input-cell"
                      v-if="show_cost_component"
                      colspan="2"
                    ></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number font-weight-bold"
                        :value="
                          total_price_cost_export_dgclass_charges.cost.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        readonly
                      />
                    </td>
                    <td
                      class="margin-cell"
                      v-if="show_cost_component"
                    >
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold"
                        :value="
                          getMargin(
                            total_price_cost_export_dgclass_charges.price,
                            total_price_cost_export_dgclass_charges.cost
                          )
                        "
                        readonly
                      />
                    </td> -->
                  </tr>

                  <template v-for="(item, i) in fc[CONSTANT.TYPE_EXPORT_DGCLASS_CHARGES]">
                    <tr
                      class="line-item-set-items"
                      v-show="showSubLineItem(showExportCargoChargesTypeItems, item)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        {{ item.description + " " + getCurrencyUnitPriceText(item) }}
                      </td>
                      <td>
                        <v-chip
                          x-small
                          label
                          outlined
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                          >{{ item.currency }}</v-chip
                        >
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.base"
                          v-model.number="item.price.base"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          class="input-number"
                          :placeholder="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.cost.base"
                          @input="handleBaseRateInput(item.cost, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          :value="item.cost.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          class="input-number"
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.cost.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="margin-cell" v-if="show_cost_component">
                        <input
                          type="text"
                          class="input-number input-number-margin"
                          :value="getMargin(item.price.converted, item.cost.converted)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>

                  <tr class="line-item-set-header blue-lighten-6">
                    <td class="input-cell" colspan="6"></td>
                    <td class="input-cell">
                      <input
                        class="input-number font-weight-bold blue-lighten-6"
                        :placeholder="
                          total_price_cost_export_dgclass_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        :value="
                          total_price_cost_export_dgclass_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        readonly
                      />
                    </td>
                    <td class="input-cell" v-if="show_cost_component" colspan="2"></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number font-weight-bold blue-lighten-6"
                        :value="
                          total_price_cost_export_dgclass_charges.cost.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        readonly
                      />
                    </td>
                    <td class="margin-cell" v-if="show_cost_component">
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold blue-lighten-6"
                        :value="
                          getMargin(
                            total_price_cost_export_dgclass_charges.price,
                            total_price_cost_export_dgclass_charges.cost
                          )
                        "
                        readonly
                      />
                    </td>
                  </tr>
                </tbody>

                <!-- Export haulage -->
                <tbody
                  class="line-item-set"
                  v-if="
                    fc.hasOwnProperty(CONSTANT.TYPE_EXPORT_HAULAGE_CHARGES) &&
                    show_export_haulage_charges &&
                    (total_price_cost_export_haulage_charges.price > 0 ||
                      total_price_cost_export_haulage_charges.total_qty > 0)
                  "
                >
                  <tr class="line-item-set-header grey lighten-4">
                    <td class="component-title-header">
                      <v-btn
                        x-small
                        icon
                        @click="showExportHaulageItems = !showExportHaulageItems"
                        style="background-color: gainsboro"
                      >
                        <v-icon>{{ !showExportHaulageItems ? "add" : "remove" }}</v-icon>
                      </v-btn>
                    </td>
                    <td
                      class="single-description description font-weight-bold"
                      colspan="2"
                    >
                      Total export haulage charges from
                      {{ quotation.city_haulage_export.name }}
                    </td>
                    <!-- <td></td> -->
                    <td class="input-cell" colspan="8"></td>
                    <!-- <td class="input-cell">
                      <input
                        class="input-number font-weight-bold"
                        :placeholder="
                          total_price_cost_export_haulage_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        :value="
                          total_price_cost_export_haulage_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        readonly
                      />
                    </td>
                    <td
                      class="input-cell"
                      v-if="show_cost_component"
                      colspan="2"
                    ></td>
                    <td
                      class="input-cell font-weight-bold"
                      v-if="show_cost_component"
                    >
                      <input
                        class="input-number"
                        :value="
                          total_price_cost_export_haulage_charges.cost.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        v-if="show_cost_component"
                        readonly
                      />
                    </td>
                    <td
                      class="margin-cell"
                      v-if="show_cost_component"
                    >
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold"
                        :value="
                          getMargin(
                            total_price_cost_export_haulage_charges.price,
                            total_price_cost_export_haulage_charges.cost
                          )
                        "
                        readonly
                      />
                    </td> -->
                  </tr>

                  <template v-for="(item, i) in fc[CONSTANT.TYPE_EXPORT_HAULAGE_CHARGES]">
                    <tr
                      class="line-item-set-items"
                      v-show="showSubLineItem(showExportHaulageItems, item)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        {{ item.description + " " + getCurrencyUnitPriceText(item) }}
                      </td>
                      <td>
                        <v-chip
                          x-small
                          label
                          outlined
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                          >{{ item.currency }}</v-chip
                        >
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.base"
                          v-model.number="item.price.base"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          class="input-number"
                          :placeholder="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.cost.base"
                          @input="handleBaseRateInput(item.cost, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          :value="item.cost.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          class="input-number"
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.cost.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="margin-cell" v-if="show_cost_component">
                        <input
                          type="text"
                          class="input-number input-number-margin"
                          :value="getMargin(item.price.converted, item.cost.converted)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>

                  <tr class="line-item-set-header blue-lighten-6">
                    <td class="input-cell" colspan="6"></td>
                    <td class="input-cell">
                      <input
                        class="input-number font-weight-bold blue-lighten-6"
                        :placeholder="
                          total_price_cost_export_haulage_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        :value="
                          total_price_cost_export_haulage_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        readonly
                      />
                    </td>
                    <td class="input-cell" v-if="show_cost_component" colspan="2"></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number font-weight-bold blue-lighten-6"
                        :value="
                          total_price_cost_export_haulage_charges.cost.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        v-if="show_cost_component"
                        readonly
                      />
                    </td>
                    <td class="margin-cell" v-if="show_cost_component">
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold blue-lighten-6"
                        :value="
                          getMargin(
                            total_price_cost_export_haulage_charges.price,
                            total_price_cost_export_haulage_charges.cost
                          )
                        "
                        readonly
                      />
                    </td>
                  </tr>
                </tbody>

                <!-- Export transport -->
                <tbody
                  class="line-item-set"
                  v-if="
                    fc.hasOwnProperty(CONSTANT.TYPE_EXPORT_TRANSPORT_CHARGES) &&
                    show_export_transport_charges &&
                    (total_price_cost_export_transport_charges.price > 0 ||
                      total_price_cost_export_transport_charges.total_qty > 0 ||
                      showCost)
                  "
                >
                  <tr
                    class="line-item-set-header grey lighten-4"
                    v-if="fc[CONSTANT.TYPE_EXPORT_TRANSPORT_CHARGES].length > 1"
                  >
                    <td class="component-title-header">
                      <v-btn
                        x-small
                        icon
                        @click="showExportTransportItems = !showExportTransportItems"
                        style="background-color: gainsboro"
                      >
                        <v-icon>{{
                          !showExportTransportItems ? "add" : "remove"
                        }}</v-icon>
                      </v-btn>
                    </td>
                    <td
                      class="single-description description font-weight-bold"
                      colspan="2"
                    >
                      Export transport charges:
                      {{
                        quotation.city_transport_export.name +
                        " - " +
                        quotation.origin_port.name
                      }}
                    </td>
                    <!-- <td></td> -->
                    <td class="input-cell" colspan="8"></td>
                    <!-- <td class="input-cell">
                      <input
                        class="input-number font-weight-bold"
                        :placeholder="
                          total_price_cost_export_transport_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        :value="
                          total_price_cost_export_transport_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        readonly
                      />
                    </td>
                    <td
                      class="input-cell"
                      v-if="show_cost_component"
                      colspan="2"
                    ></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number font-weight-bold"
                        :value="
                          total_price_cost_export_transport_charges.cost.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        readonly
                      />
                    </td>
                    <td class="margin-cell" v-if="show_cost_component">
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold"
                        :value="
                          getMargin(
                            total_price_cost_export_transport_charges.price,
                            total_price_cost_export_transport_charges.cost
                          )
                        "
                        readonly
                      />
                    </td> -->
                  </tr>

                  <template
                    v-for="(item, i) in fc[CONSTANT.TYPE_EXPORT_TRANSPORT_CHARGES]"
                  >
                    <tr
                      :class="
                        fc[CONSTANT.TYPE_EXPORT_TRANSPORT_CHARGES].length > 1
                          ? 'line-item-set-items'
                          : 'line-item-set-header blue-lighten-6'
                      "
                      v-show="showSubLineItem(true, item)"
                      :key="i"
                    >
                      <td class="single-description component-title-header">
                        <v-row
                          v-if="
                            $root.is_admin &&
                            item.code == 'TR-RAT' &&
                            quotation.export_transport_tonnage_rates.length > 0
                          "
                        >
                          <v-tooltip top>
                            <template v-slot:activator="{ on }">
                              <v-btn
                                v-on="on"
                                icon
                                x-small
                                color="primary"
                                @click="onTonnageSelection(item, 'EXPORT')"
                              >
                                <v-icon size="18">mdi-view-list</v-icon>
                              </v-btn>
                            </template>
                            Other tonnage options
                          </v-tooltip>
                        </v-row>
                      </td>
                      <td
                        v-if="fc[CONSTANT.TYPE_EXPORT_TRANSPORT_CHARGES].length > 1"
                        class="description"
                      >
                        {{ item.description + " " + getCurrencyUnitPriceText(item) }}
                      </td>
                      <td v-else class="single-description description font-weight-bold">
                        Export transport charges:
                        {{
                          quotation.city_transport_export.name +
                          " - " +
                          quotation.origin_port.name +
                          " " +
                          getCurrencyUnitPriceText(item)
                        }}
                      </td>
                      <td class="single-description">
                        <v-chip
                          x-small
                          label
                          outlined
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                          >{{ item.currency }}</v-chip
                        >
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          :class="
                            fc[CONSTANT.TYPE_EXPORT_TRANSPORT_CHARGES].length > 1
                              ? 'input-number'
                              : 'input-number blue-lighten-6'
                          "
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          :class="
                            fc[CONSTANT.TYPE_EXPORT_TRANSPORT_CHARGES].length > 1
                              ? 'input-number'
                              : 'input-number blue-lighten-6'
                          "
                          :placeholder="item.price.base"
                          v-model.number="item.price.base"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          :class="
                            fc[CONSTANT.TYPE_EXPORT_TRANSPORT_CHARGES].length > 1
                              ? 'input-number'
                              : 'input-number blue-lighten-6'
                          "
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          :class="
                            fc[CONSTANT.TYPE_EXPORT_TRANSPORT_CHARGES].length > 1
                              ? 'input-number'
                              : 'input-number font-weight-bold blue-lighten-6'
                          "
                          :placeholder="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          :class="
                            fc[CONSTANT.TYPE_EXPORT_TRANSPORT_CHARGES].length > 1
                              ? 'input-number'
                              : 'input-number blue-lighten-6'
                          "
                          v-model.number="item.cost.base"
                          @input="handleBaseRateInput(item.cost, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          :class="
                            fc[CONSTANT.TYPE_EXPORT_TRANSPORT_CHARGES].length > 1
                              ? 'input-number'
                              : 'input-number blue-lighten-6'
                          "
                          :value="item.cost.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          :class="
                            fc[CONSTANT.TYPE_EXPORT_TRANSPORT_CHARGES].length > 1
                              ? 'input-number'
                              : 'input-number font-weight-bold blue-lighten-6'
                          "
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.cost.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="margin-cell" v-if="show_cost_component">
                        <input
                          type="text"
                          :class="
                            fc[CONSTANT.TYPE_EXPORT_TRANSPORT_CHARGES].length > 1
                              ? 'input-number input-number-margin'
                              : 'input-number input-number-margin font-weight-bold blue-lighten-6'
                          "
                          :value="getMargin(item.price.converted, item.cost.converted)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>

                  <tr
                    class="line-item-set-header blue-lighten-6"
                    v-if="fc[CONSTANT.TYPE_EXPORT_TRANSPORT_CHARGES].length > 1"
                  >
                    <td class="input-cell" colspan="6"></td>
                    <td class="input-cell">
                      <input
                        class="input-number font-weight-bold blue-lighten-6"
                        :placeholder="
                          total_price_cost_export_transport_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        :value="
                          total_price_cost_export_transport_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        readonly
                      />
                    </td>
                    <td class="input-cell" v-if="show_cost_component" colspan="2"></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number font-weight-bold blue-lighten-6"
                        :value="
                          total_price_cost_export_transport_charges.cost.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        readonly
                      />
                    </td>
                    <td class="margin-cell" v-if="show_cost_component">
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold blue-lighten-6"
                        :value="
                          getMargin(
                            total_price_cost_export_transport_charges.price,
                            total_price_cost_export_transport_charges.cost
                          )
                        "
                        readonly
                      />
                    </td>
                  </tr>
                </tbody>

                <!-- Export Local charges -->
                <tbody
                  class="line-item-set"
                  v-if="
                    fc.hasOwnProperty(CONSTANT.TYPE_EXPORT_LOCAL_CHARGES) &&
                    (total_price_cost_export_local_charges.price > 0 ||
                      total_price_cost_export_local_charges.total_qty > 0)
                  "
                >
                  <tr class="line-item-set-header grey lighten-4">
                    <td class="component-title-header">
                      <v-btn
                        x-small
                        icon
                        @click="
                          showExportLocalChargesItems = !showExportLocalChargesItems
                        "
                        style="background-color: gainsboro"
                      >
                        <v-icon>{{
                          !showExportLocalChargesItems ? "add" : "remove"
                        }}</v-icon>
                      </v-btn>
                    </td>
                    <td
                      class="single-description description font-weight-bold"
                      colspan="2"
                    >
                      Export local charges at {{ quotation.origin_port.name }}
                    </td>
                    <!-- <td></td> -->
                    <td class="input-cell" colspan="8"></td>
                    <!-- <td class="input-cell">
                      <input
                        class="input-number font-weight-bold"
                        :placeholder="
                          total_price_cost_export_local_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        :value="
                          total_price_cost_export_local_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        readonly
                      />
                    </td>
                    <td
                      class="input-cell"
                      v-if="show_cost_component"
                      colspan="2"
                    ></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number font-weight-bold"
                        :value="
                          total_price_cost_export_local_charges.cost.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        readonly
                      />
                    </td>
                    <td class="margin-cell" v-if="show_cost_component">
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold"
                        :value="
                          getMargin(
                            total_price_cost_export_local_charges.price,
                            total_price_cost_export_local_charges.cost
                          )
                        "
                        readonly
                      />
                    </td> -->
                  </tr>

                  <template v-for="(item, i) in fc[CONSTANT.TYPE_EXPORT_LOCAL_CHARGES]">
                    <tr
                      class="line-item-set-items"
                      v-show="showSubLineItem(showExportLocalChargesItems, item)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        {{ item.description + " " + getCurrencyUnitPriceText(item) }}
                      </td>
                      <td>
                        <v-chip
                          x-small
                          label
                          outlined
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                          >{{ item.currency }}</v-chip
                        >
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.base"
                          v-model.number="item.price.base"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          class="input-number"
                          :placeholder="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.cost.base"
                          @input="handleBaseRateInput(item.cost, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          :value="item.cost.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="text"
                          class="input-number"
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.cost.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="margin-cell" v-if="show_cost_component">
                        <input
                          type="text"
                          class="input-number input-number-margin"
                          :value="getMargin(item.price.converted, item.cost.converted)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>

                  <tr class="line-item-set-header blue-lighten-6">
                    <td class="input-cell" colspan="6"></td>
                    <td class="input-cell">
                      <input
                        class="input-number blue-lighten-6 font-weight-bold"
                        :placeholder="
                          total_price_cost_export_local_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        :value="
                          total_price_cost_export_local_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        readonly
                      />
                    </td>
                    <td class="input-cell" v-if="show_cost_component" colspan="2"></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number blue-lighten-6 font-weight-bold"
                        :value="
                          total_price_cost_export_local_charges.cost.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        readonly
                      />
                    </td>
                    <td class="margin-cell" v-if="show_cost_component">
                      <input
                        type="text"
                        class="input-number input-number-margin blue-lighten-6 font-weight-bold"
                        :value="
                          getMargin(
                            total_price_cost_export_local_charges.price,
                            total_price_cost_export_local_charges.cost
                          )
                        "
                        readonly
                      />
                    </td>
                  </tr>
                </tbody>

                <!-- Export customs clearance -->
                <tbody
                  class="line-item-set"
                  v-if="
                    fc.hasOwnProperty(CONSTANT.TYPE_EXPORT_CUSTOMS_CHARGES) &&
                    show_export_customs_charges &&
                    (total_price_cost_export_customs_charges.price > 0 ||
                      total_price_cost_export_customs_charges.total_qty > 0)
                  "
                >
                  <tr class="line-item-set-header grey lighten-4">
                    <td class="component-title-header">
                      <v-btn
                        x-small
                        icon
                        @click="
                          showExportCustomsClearanceItems = !showExportCustomsClearanceItems
                        "
                        style="background-color: gainsboro"
                      >
                        <v-icon>{{
                          !showExportCustomsClearanceItems ? "add" : "remove"
                        }}</v-icon>
                      </v-btn>
                    </td>
                    <td
                      class="single-description description font-weight-bold"
                      colspan="2"
                    >
                      Export customs clearance at
                      {{ quotation.origin_port.name }}
                    </td>
                    <!-- <td></td> -->
                    <td class="input-cell" colspan="8"></td>
                    <!-- <td class="input-cell">
                      <input
                        class="input-number font-weight-bold"
                        :placeholder="total_price_cost_export_customs_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })"
                        :value="total_price_cost_export_customs_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })"
                        @input="handleInput"
                        readonly
                      />
                    </td>
                    <td
                      class="input-cell"
                      colspan="2"
                      v-if="show_cost_component"
                    ></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number font-weight-bold"
                        :value="total_price_cost_export_customs_charges.cost.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        @input="handleInput"
                        readonly
                      />
                    </td>
                    <td class="margin-cell" v-if="show_cost_component">
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold"
                        :value="
                          getMargin(
                            total_price_cost_export_customs_charges.price,
                            total_price_cost_export_customs_charges.cost
                          )
                        "
                        readonly
                      />
                    </td> -->
                  </tr>

                  <template v-for="(item, i) in fc[CONSTANT.TYPE_EXPORT_CUSTOMS_CHARGES]">
                    <tr
                      class="line-item-set-items"
                      v-show="showSubLineItem(showExportCustomsClearanceItems, item)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        {{ item.description + " " + getCurrencyUnitPriceText(item) }}
                      </td>
                      <td>
                        <v-chip
                          x-small
                          label
                          outlined
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                          >{{ item.currency }}</v-chip
                        >
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.base"
                          v-model.number="item.price.base"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          class="input-number"
                          :placeholder="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.cost.base"
                          @input="handleBaseRateInput(item.cost, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          :value="item.cost.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="text"
                          class="input-number"
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.cost.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="margin-cell" v-if="show_cost_component">
                        <input
                          type="text"
                          class="input-number input-number-margin"
                          :value="getMargin(item.price.converted, item.cost.converted)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>

                  <tr class="line-item-set-header blue-lighten-6">
                    <td class="input-cell" colspan="6"></td>
                    <td class="input-cell">
                      <input
                        class="input-number blue-lighten-6 font-weight-bold"
                        :placeholder="
                          total_price_cost_export_customs_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        :value="
                          total_price_cost_export_customs_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        @input="handleInput"
                        readonly
                      />
                    </td>
                    <td class="input-cell" colspan="2" v-if="show_cost_component"></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number blue-lighten-6 font-weight-bold"
                        :value="
                          total_price_cost_export_customs_charges.cost.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        @input="handleInput"
                        readonly
                      />
                    </td>
                    <td class="margin-cell" v-if="show_cost_component">
                      <input
                        type="text"
                        class="input-number input-number-margin blue-lighten-6 font-weight-bold"
                        :value="
                          getMargin(
                            total_price_cost_export_customs_charges.price,
                            total_price_cost_export_customs_charges.cost
                          )
                        "
                        readonly
                      />
                    </td>
                  </tr>
                </tbody>

                <!-- Import Cargo type charges -->
                <tbody
                  class="line-item-set"
                  v-if="
                    fc.hasOwnProperty(CONSTANT.TYPE_IMPORT_DGCLASS_CHARGES) &&
                    show_import_dgclass_charges &&
                    (total_price_cost_import_dgclass_charges.price > 0 ||
                      total_price_cost_import_dgclass_charges.total_qty > 0)
                  "
                >
                  <tr class="line-item-set-header grey lighten-4">
                    <td class="component-title-header">
                      <v-btn
                        x-small
                        icon
                        @click="
                          showImportCargoChargesTypeItems = !showImportCargoChargesTypeItems
                        "
                        style="background-color: gainsboro"
                      >
                        <v-icon>{{
                          !showImportCargoChargesTypeItems ? "add" : "remove"
                        }}</v-icon>
                      </v-btn>
                    </td>
                    <td
                      class="single-description description font-weight-bold"
                      colspan="2"
                    >
                      Import cargo specific charges at
                      {{ quotation.destination_port.name }}
                    </td>
                    <!-- <td></td> -->
                    <td class="input-cell" colspan="8"></td>
                    <!-- <td class="input-cell">
                      <input
                        class="input-number font-weight-bold"
                        :placeholder="
                          total_price_cost_import_dgclass_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        :value="
                          total_price_cost_import_dgclass_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        readonly
                      />
                    </td>
                    <td
                      class="input-cell"
                      v-if="show_cost_component"
                      colspan="2"
                    ></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number font-weight-bold"
                        :value="
                          total_price_cost_import_dgclass_charges.cost.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        readonly
                      />
                    </td>
                    <td class="margin-cell" v-if="show_cost_component">
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold"
                        :value="
                          getMargin(
                            total_price_cost_import_dgclass_charges.price,
                            total_price_cost_import_dgclass_charges.cost
                          )
                        "
                        readonly
                      />
                    </td> -->
                  </tr>

                  <template v-for="(item, i) in fc[CONSTANT.TYPE_IMPORT_DGCLASS_CHARGES]">
                    <tr
                      class="line-item-set-items"
                      v-show="showSubLineItem(showImportCargoChargesTypeItems, item)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        {{ item.description + " " + getCurrencyUnitPriceText(item) }}
                      </td>
                      <td>
                        <v-chip
                          x-small
                          label
                          outlined
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                          >{{ item.currency }}</v-chip
                        >
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.base"
                          v-model.number="item.price.base"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          class="input-number"
                          :placeholder="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.cost.base"
                          @input="handleBaseRateInput(item.cost, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          :value="item.cost.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          class="input-number"
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.cost.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="margin-cell" v-if="show_cost_component">
                        <input
                          type="text"
                          class="input-number input-number-margin"
                          :value="getMargin(item.price.converted, item.cost.converted)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>

                  <tr class="line-item-set-header blue-lighten-6">
                    <td class="input-cell" colspan="6"></td>
                    <td class="input-cell">
                      <input
                        class="input-number font-weight-bold blue-lighten-6"
                        :placeholder="
                          total_price_cost_import_dgclass_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        :value="
                          total_price_cost_import_dgclass_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        readonly
                      />
                    </td>
                    <td class="input-cell" v-if="show_cost_component" colspan="2"></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number font-weight-bold blue-lighten-6"
                        :value="
                          total_price_cost_import_dgclass_charges.cost.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        readonly
                      />
                    </td>
                    <td class="margin-cell" v-if="show_cost_component">
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold blue-lighten-6"
                        :value="
                          getMargin(
                            total_price_cost_import_dgclass_charges.price,
                            total_price_cost_import_dgclass_charges.cost
                          )
                        "
                        readonly
                      />
                    </td>
                  </tr>
                </tbody>

                <!-- Import customs clearance -->
                <tbody
                  class="line-item-set"
                  v-if="
                    fc.hasOwnProperty(CONSTANT.TYPE_IMPORT_CUSTOMS_CHARGES) &&
                    show_import_customs_charges &&
                    (total_price_cost_import_customs_charges.price > 0 ||
                      total_price_cost_import_customs_charges.total_qty > 0)
                  "
                >
                  <tr class="line-item-set-header grey lighten-4">
                    <td class="component-title-header">
                      <v-btn
                        x-small
                        icon
                        @click="
                          showImportCustomsClearanceItems = !showImportCustomsClearanceItems
                        "
                        style="background-color: gainsboro"
                      >
                        <v-icon>{{
                          !showImportCustomsClearanceItems ? "add" : "remove"
                        }}</v-icon>
                      </v-btn>
                    </td>
                    <td
                      class="single-description description font-weight-bold"
                      colspan="2"
                    >
                      Import customs clearance at
                      {{ quotation.destination_port.name }}
                    </td>
                    <!-- <td></td> -->
                    <td class="input-cell" colspan="8"></td>
                    <!-- <td class="input-cell">
                      <input
                        class="input-number font-weight-bold"
                        :placeholder="
                          total_price_cost_import_customs_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        :value="
                          total_price_cost_import_customs_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        @input="handleInput"
                        readonly
                      />
                    </td>
                    <td
                      class="input-cell"
                      v-if="show_cost_component"
                      colspan="2"
                    ></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number font-weight-bold"
                        :value="
                          total_price_cost_import_customs_charges.cost.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        @input="handleInput"
                        readonly
                      />
                    </td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold"
                        :value="
                          getMargin(
                            total_price_cost_import_customs_charges.price,
                            total_price_cost_import_customs_charges.cost
                          )
                        "
                        readonly
                      />
                    </td> -->
                  </tr>

                  <template v-for="(item, i) in fc[CONSTANT.TYPE_IMPORT_CUSTOMS_CHARGES]">
                    <tr
                      class="line-item-set-items"
                      v-show="showSubLineItem(showImportCustomsClearanceItems, item)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        {{ item.description + " " + getCurrencyUnitPriceText(item) }}
                      </td>
                      <td>
                        <v-chip
                          x-small
                          label
                          outlined
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                          >{{ item.currency }}</v-chip
                        >
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.base"
                          v-model.number="item.price.base"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          class="input-number"
                          :placeholder="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.cost.base"
                          @input="handleBaseRateInput(item.cost, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          :value="item.cost.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          class="input-number"
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.cost.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="margin-cell" v-if="show_cost_component">
                        <input
                          type="text"
                          class="input-number input-number-margin"
                          :value="getMargin(item.price.converted, item.cost.converted)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>

                  <tr class="line-item-set-header blue-lighten-6">
                    <td class="input-cell" colspan="6"></td>
                    <td class="input-cell">
                      <input
                        class="input-number blue-lighten-6 font-weight-bold"
                        :placeholder="
                          total_price_cost_import_customs_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        :value="
                          total_price_cost_import_customs_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        @input="handleInput"
                        readonly
                      />
                    </td>
                    <td class="input-cell" v-if="show_cost_component" colspan="2"></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number blue-lighten-6 font-weight-bold"
                        :value="
                          total_price_cost_import_customs_charges.cost.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        @input="handleInput"
                        readonly
                      />
                    </td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        type="text"
                        class="input-number input-number-margin blue-lighten-6 font-weight-bold"
                        :value="
                          getMargin(
                            total_price_cost_import_customs_charges.price,
                            total_price_cost_import_customs_charges.cost
                          )
                        "
                        readonly
                      />
                    </td>
                  </tr>
                </tbody>

                <!-- Import Local charges -->
                <tbody
                  class="line-item-set"
                  v-if="
                    fc.hasOwnProperty(CONSTANT.TYPE_IMPORT_LOCAL_CHARGES) &&
                    (total_price_cost_import_local_charges.price > 0 ||
                      total_price_cost_import_local_charges.total_qty > 0)
                  "
                >
                  <tr class="line-item-set-header grey lighten-4">
                    <td class="component-title-header">
                      <v-btn
                        x-small
                        icon
                        @click="
                          showImportLocalChargesItems = !showImportLocalChargesItems
                        "
                        style="background-color: gainsboro"
                      >
                        <v-icon>{{
                          !showImportLocalChargesItems ? "add" : "remove"
                        }}</v-icon>
                      </v-btn>
                    </td>
                    <td
                      class="single-description description font-weight-bold"
                      colspan="2"
                    >
                      Import local charges at
                      {{ quotation.destination_port.name }}
                    </td>
                    <!-- <td></td> -->
                    <td class="input-cell" colspan="8"></td>
                    <!-- <td class="input-cell">
                      <input
                        class="input-number font-weight-bold"
                        :placeholder="
                          total_price_cost_import_local_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        :value="
                          total_price_cost_import_local_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        readonly
                      />
                    </td>
                    <td
                      class="input-cell"
                      v-if="show_cost_component"
                      colspan="2"
                    ></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number font-weight-bold"
                        :value="
                          total_price_cost_import_local_charges.cost.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        readonly
                      />
                    </td>
                    <td class="margin-cell" v-if="show_cost_component">
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold"
                        :value="
                          getMargin(
                            total_price_cost_import_local_charges.price,
                            total_price_cost_import_local_charges.cost
                          )
                        "
                        readonly
                      />
                    </td> -->
                  </tr>

                  <template v-for="(item, i) in fc[CONSTANT.TYPE_IMPORT_LOCAL_CHARGES]">
                    <tr
                      class="line-item-set-items"
                      v-show="showSubLineItem(showImportLocalChargesItems, item)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        {{ item.description + " " + getCurrencyUnitPriceText(item) }}
                      </td>
                      <td>
                        <v-chip
                          x-small
                          label
                          outlined
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                          >{{ item.currency }}</v-chip
                        >
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.base"
                          v-model.number="item.price.base"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          class="input-number"
                          :placeholder="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.cost.base"
                          @input="handleBaseRateInput(item.cost, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          :value="item.cost.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          class="input-number"
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.cost.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="margin-cell" v-if="show_cost_component">
                        <input
                          type="text"
                          class="input-number input-number-margin"
                          :value="getMargin(item.price.converted, item.cost.converted)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>

                  <tr class="line-item-set-header blue-lighten-6">
                    <td class="input-cell" colspan="6"></td>
                    <td class="input-cell">
                      <input
                        class="input-number blue-lighten-6 font-weight-bold"
                        :placeholder="
                          total_price_cost_import_local_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        :value="
                          total_price_cost_import_local_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        readonly
                      />
                    </td>
                    <td class="input-cell" v-if="show_cost_component" colspan="2"></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number blue-lighten-6 font-weight-bold"
                        :value="
                          total_price_cost_import_local_charges.cost.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        readonly
                      />
                    </td>
                    <td class="margin-cell" v-if="show_cost_component">
                      <input
                        type="text"
                        class="input-number input-number-margin blue-lighten-6 font-weight-bold"
                        :value="
                          getMargin(
                            total_price_cost_import_local_charges.price,
                            total_price_cost_import_local_charges.cost
                          )
                        "
                        readonly
                      />
                    </td>
                  </tr>
                </tbody>

                <!-- Import haulage -->
                <tbody
                  class="line-item-set"
                  v-if="
                    fc.hasOwnProperty(CONSTANT.TYPE_IMPORT_HAULAGE_CHARGES) &&
                    show_import_haulage_charges &&
                    (total_price_cost_import_haulage_charges.price > 0 ||
                      total_price_cost_import_haulage_charges.total_qty > 0)
                  "
                >
                  <tr class="line-item-set-header grey lighten-4">
                    <td class="component-title-header">
                      <v-btn
                        x-small
                        icon
                        @click="showImportHaulageItems = !showImportHaulageItems"
                        style="background-color: gainsboro"
                      >
                        <v-icon>{{ !showImportHaulageItems ? "add" : "remove" }}</v-icon>
                      </v-btn>
                    </td>
                    <td
                      class="single-description description font-weight-bold"
                      colspan="2"
                    >
                      Total import haulage charges to
                      {{ quotation.city_haulage_import.name }}
                    </td>
                    <!-- <td></td> -->
                    <td class="input-cell" colspan="8"></td>
                    <!-- <td class="input-cell">
                      <input
                        class="input-number input-number-total font-weight-bold"
                        :placeholder="
                          total_price_cost_import_haulage_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        :value="
                          total_price_cost_import_haulage_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        @input="handleInput"
                        readonly
                      />
                    </td>
                    <td
                      class="input-cell"
                      v-if="show_cost_component"
                      colspan="2"
                    ></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number input-number-total font-weight-bold"
                        :value="
                          total_price_cost_import_haulage_charges.cost.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        @input="handleInput"
                        readonly
                      />
                    </td>
                    <td class="margin-cell" v-if="show_cost_component">
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold"
                        :value="
                          getMargin(
                            total_price_cost_import_haulage_charges.price,
                            total_price_cost_import_haulage_charges.cost
                          )
                        "
                        readonly
                      />
                    </td> -->
                  </tr>

                  <template v-for="(item, i) in fc[CONSTANT.TYPE_IMPORT_HAULAGE_CHARGES]">
                    <tr
                      class="line-item-set-items"
                      v-show="showSubLineItem(showImportHaulageItems, item)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        {{ item.description + " " + getCurrencyUnitPriceText(item) }}
                      </td>
                      <td>
                        <v-chip
                          x-small
                          label
                          outlined
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                          >{{ item.currency }}</v-chip
                        >
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.base"
                          v-model.number="item.price.base"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          class="input-number"
                          :placeholder="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.cost.base"
                          @input="handleBaseRateInput(item.cost, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          :value="item.cost.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          class="input-number"
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.cost.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="margin-cell" v-if="show_cost_component">
                        <input
                          type="text"
                          class="input-number input-number-margin"
                          :value="getMargin(item.price.converted, item.cost.converted)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>

                  <tr class="line-item-set-header blue-lighten-6">
                    <td class="input-cell" colspan="6"></td>
                    <td class="input-cell">
                      <input
                        class="input-number font-weight-bold blue-lighten-6"
                        :placeholder="
                          total_price_cost_import_haulage_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        :value="
                          total_price_cost_import_haulage_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        @input="handleInput"
                        readonly
                      />
                    </td>
                    <td class="input-cell" v-if="show_cost_component" colspan="2"></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number font-weight-bold blue-lighten-6"
                        :value="
                          total_price_cost_import_haulage_charges.cost.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        @input="handleInput"
                        readonly
                      />
                    </td>
                    <td class="margin-cell" v-if="show_cost_component">
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold blue-lighten-6"
                        :value="
                          getMargin(
                            total_price_cost_import_haulage_charges.price,
                            total_price_cost_import_haulage_charges.cost
                          )
                        "
                        readonly
                      />
                    </td>
                  </tr>
                </tbody>

                <!-- Import transport -->
                <tbody
                  class="line-item-set"
                  v-if="
                    fc.hasOwnProperty(CONSTANT.TYPE_IMPORT_TRANSPORT_CHARGES) &&
                    show_import_transport_charges &&
                    (total_price_cost_import_transport_charges.price > 0 ||
                      total_price_cost_import_transport_charges.total_qty > 0 ||
                      showCost)
                  "
                >
                  <tr
                    class="line-item-set-header grey lighten-4"
                    v-if="fc[CONSTANT.TYPE_IMPORT_TRANSPORT_CHARGES].length > 1"
                  >
                    <td class="component-title-header">
                      <v-btn
                        x-small
                        icon
                        @click="showImportTransportItems = !showImportTransportItems"
                        style="background-color: gainsboro"
                      >
                        <v-icon>{{
                          !showImportTransportItems ? "add" : "remove"
                        }}</v-icon>
                      </v-btn>
                    </td>
                    <td
                      class="single-description description font-weight-bold"
                      colspan="2"
                    >
                      Import transport charges:
                      {{
                        quotation.destination_port.name +
                        " - " +
                        quotation.city_transport_import.name
                      }}
                    </td>
                    <!-- <td></td> -->
                    <td class="input-cell" colspan="8"></td>
                    <!-- <td class="input-cell">
                      <input
                        class="input-number font-weight-bold"
                        :placeholder="
                          total_price_cost_import_transport_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        :value="
                          total_price_cost_import_transport_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        readonly
                      />
                    </td>
                    <td
                      class="input-cell"
                      v-if="show_cost_component"
                      colspan="2"
                    ></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number font-weight-bold"
                        :value="
                          total_price_cost_import_transport_charges.cost.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        readonly
                      />
                    </td>
                    <td class="margin-cell" v-if="show_cost_component">
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold"
                        :value="
                          getMargin(
                            total_price_cost_import_transport_charges.price,
                            total_price_cost_import_transport_charges.cost
                          )
                        "
                        readonly
                      />
                    </td> -->
                  </tr>

                  <template
                    v-for="(item, i) in fc[CONSTANT.TYPE_IMPORT_TRANSPORT_CHARGES]"
                  >
                    <tr
                      :class="
                        fc[CONSTANT.TYPE_IMPORT_TRANSPORT_CHARGES].length > 1
                          ? 'line-item-set-items'
                          : 'line-item-set-header blue-lighten-6'
                      "
                      v-show="showSubLineItem(true, item)"
                      :key="i"
                    >
                      <td class="single-description component-title-header">
                        <v-row
                          v-if="
                            $root.is_admin &&
                            item.code == 'TR-RAT' &&
                            quotation.import_transport_tonnage_rates
                          "
                        >
                          <v-tooltip top>
                            <template v-slot:activator="{ on }">
                              <v-btn
                                v-on="on"
                                icon
                                x-small
                                color="primary"
                                @click="onTonnageSelection(item, 'IMPORT')"
                              >
                                <v-icon size="18">mdi-view-list</v-icon>
                              </v-btn>
                            </template>
                            Other tonnage options
                          </v-tooltip>
                        </v-row>
                      </td>
                      <td
                        v-if="fc[CONSTANT.TYPE_IMPORT_TRANSPORT_CHARGES].length > 1"
                        class="description"
                      >
                        {{ item.description + " " + getCurrencyUnitPriceText(item) }}
                      </td>
                      <td v-else class="single-description description font-weight-bold">
                        Import transport charges:
                        {{
                          quotation.destination_port.name +
                          " - " +
                          quotation.city_transport_import.name +
                          " " +
                          getCurrencyUnitPriceText(item)
                        }}
                      </td>
                      <td class="single-description">
                        <v-chip
                          x-small
                          label
                          outlined
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                          >{{ item.currency }}</v-chip
                        >
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          :class="
                            fc[CONSTANT.TYPE_IMPORT_TRANSPORT_CHARGES].length > 1
                              ? 'input-number'
                              : 'input-number blue-lighten-6'
                          "
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          :class="
                            fc[CONSTANT.TYPE_IMPORT_TRANSPORT_CHARGES].length > 1
                              ? 'input-number'
                              : 'input-number blue-lighten-6'
                          "
                          :placeholder="item.price.base"
                          v-model.number="item.price.base"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          :class="
                            fc[CONSTANT.TYPE_IMPORT_TRANSPORT_CHARGES].length > 1
                              ? 'input-number'
                              : 'input-number blue-lighten-6'
                          "
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          :class="
                            fc[CONSTANT.TYPE_IMPORT_TRANSPORT_CHARGES].length > 1
                              ? 'input-number'
                              : 'input-number font-weight-bold blue-lighten-6'
                          "
                          :placeholder="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          :class="
                            fc[CONSTANT.TYPE_IMPORT_TRANSPORT_CHARGES].length > 1
                              ? 'input-number'
                              : 'input-number blue-lighten-6'
                          "
                          v-model.number="item.cost.base"
                          @input="handleBaseRateInput(item.cost, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          :class="
                            fc[CONSTANT.TYPE_IMPORT_TRANSPORT_CHARGES].length > 1
                              ? 'input-number'
                              : 'input-number blue-lighten-6'
                          "
                          :value="item.cost.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          :class="
                            fc[CONSTANT.TYPE_IMPORT_TRANSPORT_CHARGES].length > 1
                              ? 'input-number'
                              : 'input-number font-weight-bold blue-lighten-6'
                          "
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.cost.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="margin-cell" v-if="show_cost_component">
                        <input
                          type="text"
                          :class="
                            fc[CONSTANT.TYPE_IMPORT_TRANSPORT_CHARGES].length > 1
                              ? 'input-number input-number-margin'
                              : 'input-number input-number-margin font-weight-bold blue-lighten-6'
                          "
                          :value="getMargin(item.price.converted, item.cost.converted)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>

                  <tr
                    class="line-item-set-header blue-lighten-6"
                    v-if="fc[CONSTANT.TYPE_IMPORT_TRANSPORT_CHARGES].length > 1"
                  >
                    <td class="input-cell" colspan="6"></td>
                    <td class="input-cell">
                      <input
                        class="input-number font-weight-bold blue-lighten-6"
                        :placeholder="
                          total_price_cost_import_transport_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        :value="
                          total_price_cost_import_transport_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        readonly
                      />
                    </td>
                    <td class="input-cell" v-if="show_cost_component" colspan="2"></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number font-weight-bold blue-lighten-6"
                        :value="
                          total_price_cost_import_transport_charges.cost.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        readonly
                      />
                    </td>
                    <td class="margin-cell" v-if="show_cost_component">
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold blue-lighten-6"
                        :value="
                          getMargin(
                            total_price_cost_import_transport_charges.price,
                            total_price_cost_import_transport_charges.cost
                          )
                        "
                        readonly
                      />
                    </td>
                  </tr>
                </tbody>

                <!-- Other Services Charges Component -->
                <tbody
                  v-if="
                    fc.hasOwnProperty(CONSTANT.TYPE_OTHER_SERVICES_CHARGES) &&
                    show_other_services_charges &&
                    (total_price_cost_other_services_charges.price > 0 ||
                      total_price_cost_other_services_charges.total_qty > 0 ||
                      showCost)
                  "
                  class="line-item-set"
                >
                  <tr class="line-item-set-header grey lighten-4">
                    <td class="component-title-header">
                      <v-btn
                        x-small
                        icon
                        @click="showOtherServices = !showOtherServices"
                        style="background-color: gainsboro"
                      >
                        <v-icon>{{ !showOtherServices ? "add" : "remove" }}</v-icon>
                      </v-btn>
                    </td>
                    <td
                      class="single-description description font-weight-bold"
                      colspan="2"
                    >
                      <span>Other items/services &nbsp;&nbsp;&nbsp;</span>
                      <v-btn
                        x-small
                        icon
                        dark
                        @click="
                          addComponent(
                            CONSTANT.TYPE_OTHER_SERVICES_CHARGES,
                            'OT-SER',
                            'SET'
                          )
                        "
                        v-if="showCost"
                        style="background-color: #1976d2"
                        class="add-remove-item"
                      >
                        <v-icon size="20" color="white">add</v-icon>
                      </v-btn>
                    </td>
                    <!-- <td></td> -->
                    <td class="input-cell" colspan="8"></td>
                    <!-- <td class="input-cell">
                      <input
                        type="number"
                        class="input-number font-weight-bold"
                        :placeholder="
                          total_price_cost_other_services_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        :value="
                          total_price_cost_other_services_charges.price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        readonly
                      />
                    </td>
                    <td
                      class="input-cell"
                      v-if="show_cost_component"
                      colspan="2"
                    ></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        type="number"
                        class="input-number font-weight-bold"
                        :value="
                          total_price_cost_other_services_charges.cost.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        readonly
                      />
                    </td>
                    <td class="margin-cell" v-if="show_cost_component">
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold"
                        :value="
                          getMargin(
                            total_price_cost_other_services_charges.price,
                            total_price_cost_other_services_charges.cost
                          )
                        "
                        readonly
                      />
                    </td> -->
                  </tr>

                  <template v-for="(item, i) in fc[CONSTANT.TYPE_OTHER_SERVICES_CHARGES]">
                    <tr
                      class="line-item-set-items"
                      v-show="showSubLineItem(showOtherServices, item)"
                      :key="i"
                    >
                      <td></td>
                      <td class="description">
                        <div v-if="showCost">
                          <input
                            type="text"
                            class="input-description"
                            @input="handleInput"
                            :placeholder="item.description"
                            v-model="item.description"
                          />
                          <v-btn
                            x-small
                            icon
                            @click="removeComponent(item)"
                            style="background-color: #ff5252"
                            class="add-remove-item"
                          >
                            <v-icon size="18" color="white">remove</v-icon>
                          </v-btn>
                        </div>
                        <div v-else>{{ item.description }}</div>
                      </td>
                      <td>
                        <v-chip
                          x-small
                          label
                          outlined
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                          >{{ item.currency }}</v-chip
                        >
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          @input="handleInput"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.base"
                          v-model.number="item.price.base"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          class="input-number"
                          :placeholder="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.price.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          v-model.number="item.cost.base"
                          @input="handleBaseRateInput(item.cost, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number"
                          :value="item.cost.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          class="input-number"
                          :value="
                            getTotalRate(
                              item.quantity,
                              item.cost.converted
                            ).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="margin-cell" v-if="show_cost_component">
                        <input
                          type="text"
                          class="input-number input-number-margin"
                          :value="getMargin(item.price.converted, item.cost.converted)"
                          readonly
                        />
                      </td>
                    </tr>
                  </template>

                  <tr class="line-item-set-header blue-lighten-6">
                    <td class="input-cell" colspan="6"></td>
                    <td class="input-cell">
                      <input
                        class="input-number blue-lighten-6 font-weight-bold"
                        :placeholder="
                          total_price_cost_other_services_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        :value="
                          total_price_cost_other_services_charges.price.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        readonly
                      />
                    </td>
                    <td class="input-cell" v-if="show_cost_component" colspan="2"></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number font-weight-bold blue-lighten-6"
                        :value="
                          total_price_cost_other_services_charges.cost.toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        "
                        readonly
                      />
                    </td>
                    <td class="margin-cell" v-if="show_cost_component">
                      <input
                        class="input-number input-number-margin font-weight-bold blue-lighten-6"
                        :value="
                          getMargin(
                            total_price_cost_other_services_charges.price,
                            total_price_cost_other_services_charges.cost
                          )
                        "
                        readonly
                      />
                    </td>
                  </tr>
                </tbody>

                <!-- Storage Charges -->
                <tbody
                  class="line-item-single"
                  v-if="
                    fc.hasOwnProperty(CONSTANT.TYPE_STORAGE_CHARGES) &&
                    (total_price_cost_storage_charges.price > 0 ||
                      total_price_cost_storage_charges.total_qty > 0 ||
                      showCost)
                  "
                >
                  <template v-for="(item, i) in fc[CONSTANT.TYPE_STORAGE_CHARGES]">
                    <tr
                      :key="i"
                      v-show="showSubLineItem(true, item)"
                      class="blue-lighten-6"
                    >
                      <td class="component-title-header single-description"></td>
                      <td class="single-description description font-weight-bold">
                        Storage charges {{ getCurrencyUnitPriceText(item) }}
                      </td>

                      <td class="single-description">
                        <v-chip
                          x-small
                          label
                          outlined
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                          >{{ item.currency }}</v-chip
                        >
                      </td>

                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          @input="handleInput()"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          :readonly="!show_cost_component"
                          :placeholder="item.price.base"
                          v-model.number="item.price.base"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          class="input-number input-number-total font-weight-bold blue-lighten-6"
                          :placeholder="
                            total_price_cost_storage_charges.price.toLocaleString(
                              'en-US',
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )
                          "
                          :value="
                            total_price_cost_storage_charges.price.toLocaleString(
                              'en-US',
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )
                          "
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          v-model.number="item.cost.base"
                          @input="handleBaseRateInput(item.cost, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          :value="item.cost.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          class="input-number input-number-total font-weight-bold blue-lighten-6"
                          :value="
                            total_price_cost_storage_charges.cost.toLocaleString(
                              'en-US',
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )
                          "
                          readonly
                        />
                      </td>
                      <td class="margin-cell" v-if="show_cost_component">
                        <input
                          type="text"
                          class="input-number input-number-margin font-weight-bold blue-lighten-6"
                          :value="
                            getMargin(
                              total_price_cost_storage_charges.price,
                              total_price_cost_storage_charges.cost
                            )
                          "
                          readonly
                        />
                      </td>
                    </tr>
                  </template>
                </tbody>

                <!-- Removal Charges -->
                <tbody
                  class="line-item-single"
                  v-if="
                    fc.hasOwnProperty(CONSTANT.TYPE_REMOVAL_CHARGES) &&
                    (total_price_cost_removal_charges.price > 0 ||
                      total_price_cost_removal_charges.total_qty > 0 ||
                      showCost)
                  "
                >
                  <template v-for="(item, i) in fc[CONSTANT.TYPE_REMOVAL_CHARGES]">
                    <tr
                      :key="i"
                      v-show="showSubLineItem(true, item)"
                      class="blue-lighten-6"
                    >
                      <td class="component-title-header single-description"></td>
                      <td class="single-description description font-weight-bold">
                        Removal charges {{ getCurrencyUnitPriceText(item) }}
                      </td>

                      <td class="single-description">
                        <v-chip
                          x-small
                          label
                          outlined
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                          >{{ item.currency }}</v-chip
                        >
                      </td>

                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          @input="handleInput()"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          :readonly="!show_cost_component"
                          :placeholder="item.price.base"
                          v-model.number="item.price.base"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          class="input-number input-number-total font-weight-bold blue-lighten-6"
                          :placeholder="
                            total_price_cost_removal_charges.price.toLocaleString(
                              'en-US',
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )
                          "
                          :value="
                            total_price_cost_removal_charges.price.toLocaleString(
                              'en-US',
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )
                          "
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          v-model.number="item.cost.base"
                          @input="handleBaseRateInput(item.cost, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          :value="item.cost.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          class="input-number input-number-total font-weight-bold blue-lighten-6"
                          :value="
                            total_price_cost_removal_charges.cost.toLocaleString(
                              'en-US',
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )
                          "
                          readonly
                        />
                      </td>
                      <td class="margin-cell" v-if="show_cost_component">
                        <input
                          type="text"
                          class="input-number input-number-margin font-weight-bold blue-lighten-6"
                          :value="
                            getMargin(
                              total_price_cost_removal_charges.price,
                              total_price_cost_removal_charges.cost
                            )
                          "
                          readonly
                        />
                      </td>
                    </tr>
                  </template>
                </tbody>

                <!-- Handling Charges -->
                <tbody
                  class="line-item-single"
                  v-if="
                    fc.hasOwnProperty(CONSTANT.TYPE_HANDLING_CHARGES) &&
                    (total_price_cost_handling_charges.price > 0 ||
                      total_price_cost_handling_charges.total_qty > 0 ||
                      showCost)
                  "
                >
                  <template v-for="(item, i) in fc[CONSTANT.TYPE_HANDLING_CHARGES]">
                    <tr
                      :key="i"
                      v-show="showSubLineItem(true, item)"
                      class="blue-lighten-6"
                    >
                      <td class="component-title-header single-description"></td>
                      <td class="single-description description font-weight-bold">
                        Shipment handling fee
                        {{ getCurrencyUnitPriceText(item) }}
                      </td>

                      <td class="single-description">
                        <v-chip
                          x-small
                          label
                          outlined
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                          >{{ item.currency }}</v-chip
                        >
                      </td>

                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          @input="handleInput()"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          :readonly="!show_cost_component"
                          :placeholder="item.price.base"
                          v-model.number="item.price.base"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          class="input-number input-number-total font-weight-bold blue-lighten-6"
                          :placeholder="
                            total_price_cost_handling_charges.price.toLocaleString(
                              'en-US',
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )
                          "
                          :value="
                            total_price_cost_handling_charges.price.toLocaleString(
                              'en-US',
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )
                          "
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          v-model.number="item.cost.base"
                          @input="handleBaseRateInput(item.cost, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          :value="item.cost.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          class="input-number input-number-total font-weight-bold blue-lighten-6"
                          :value="
                            total_price_cost_handling_charges.cost.toLocaleString(
                              'en-US',
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )
                          "
                          readonly
                        />
                      </td>
                      <td class="margin-cell" v-if="show_cost_component">
                        <input
                          type="text"
                          class="input-number input-number-margin font-weight-bold blue-lighten-6"
                          :value="
                            getMargin(
                              total_price_cost_handling_charges.price,
                              total_price_cost_handling_charges.cost
                            )
                          "
                          readonly
                        />
                      </td>
                    </tr>
                  </template>
                </tbody>

                <!-- General item(s) -->
                <tbody
                  class="line-item-single"
                  v-if="fc.hasOwnProperty(CONSTANT.TYPE_GENERAL_ITEM)"
                >
                  <template v-for="(item, i) in fc[CONSTANT.TYPE_GENERAL_ITEM]">
                    <tr
                      :key="i"
                      v-show="showSubLineItem(true, item)"
                      class="blue-lighten-6"
                    >
                      <td class="component-title-header single-description"></td>
                      <td class="single-description description font-weight-bold">
                        <div v-if="showCost">
                          <input
                            type="text"
                            class="input-description editable-description"
                            @input="handleInput"
                            :placeholder="item.description"
                            v-model="item.description"
                          />
                          <!-- <span>
                            {{ getCurrencyUnitPriceText(item) }}
                          </span> -->
                          <v-btn
                            x-small
                            icon
                            @click="removeComponent(item)"
                            style="background-color: #ff5252"
                            class="add-remove-item"
                          >
                            <v-icon size="18" color="white">remove</v-icon>
                          </v-btn>
                        </div>
                        <div v-else>{{ item.description }}</div>
                      </td>
                      <!-- <td class="single-description description font-weight-bold">
                        {{item.description}}
                        {{ getCurrencyUnitPriceText(item) }}
                      </td> -->

                      <td class="single-description">
                        <v-chip
                          x-small
                          label
                          outlined
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                          >{{ item.currency }}</v-chip
                        >
                      </td>

                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          @input="handleInput()"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          :readonly="!show_cost_component"
                          :placeholder="item.price.base"
                          v-model.number="item.price.base"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          class="input-number input-number-total font-weight-bold blue-lighten-6"
                          :placeholder="
                            getComponentPriceTotal(item).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          :value="
                            getComponentPriceTotal(item).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          v-model.number="item.cost.base"
                          @input="handleBaseRateInput(item.cost, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          :value="item.cost.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          class="input-number input-number-total font-weight-bold blue-lighten-6"
                          :value="
                            getComponentCostTotal(item).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="margin-cell" v-if="show_cost_component">
                        <input
                          type="text"
                          class="input-number input-number-margin font-weight-bold blue-lighten-6"
                          :value="
                            getMargin(
                              getComponentPriceTotal(item),
                              getComponentCostTotal(item)
                            )
                          "
                          readonly
                        />
                      </td>
                    </tr>
                  </template>
                </tbody>

                <!-- Discount & Rebate-->
                <tbody
                  class="line-item-single"
                  v-if="fc.hasOwnProperty(CONSTANT.TYPE_DISCOUNT_REBATE)"
                >
                  <template v-for="(item, i) in fc[CONSTANT.TYPE_DISCOUNT_REBATE]">
                    <tr
                      :key="i"
                      v-show="showSubLineItem(true, item)"
                      class="blue-lighten-6"
                    >
                      <td class="component-title-header single-description"></td>
                      <td class="single-description description font-weight-bold">
                        <div v-if="showCost">
                          <input
                            type="text"
                            class="input-description"
                            @input="handleInput"
                            :placeholder="item.description"
                            v-model="item.description"
                          />
                          <v-btn
                            x-small
                            icon
                            @click="removeComponent(item)"
                            style="background-color: #ff5252"
                            class="add-remove-item"
                          >
                            <v-icon size="18" color="white">remove</v-icon>
                          </v-btn>
                        </div>
                        <div v-else>{{ item.description }}</div>
                      </td>
                      <td class="single-description">
                        <v-chip
                          x-small
                          label
                          outlined
                          :color="getCurrencyColor(item).color"
                          class="font-weight-bold caption px-1"
                          @click="onOpenConvertedcurrenciesDialog(item)"
                          >{{ item.currency }}</v-chip
                        >
                      </td>

                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          @input="handleInput()"
                          :placeholder="item.quantity"
                          v-model.number="item.quantity"
                          :readonly="!show_cost_component"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          :readonly="!show_cost_component"
                          :placeholder="item.price.base"
                          v-model.number="item.price.base"
                          @input="handleBaseRateInput(item.price, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          :placeholder="item.price.converted.toFixed(2)"
                          :value="item.price.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell">
                        <input
                          class="input-number input-number-total font-weight-bold blue-lighten-6"
                          :placeholder="
                            getComponentPriceTotal(item).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          :value="
                            getComponentPriceTotal(item).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          v-model.number="item.cost.base"
                          @input="handleBaseRateInput(item.cost, item.conversion_factor)"
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          type="number"
                          class="input-number blue-lighten-6"
                          :value="item.cost.converted.toFixed(2)"
                          readonly
                        />
                      </td>
                      <td class="input-cell" v-if="show_cost_component">
                        <input
                          class="input-number input-number-total font-weight-bold blue-lighten-6"
                          :value="
                            getComponentCostTotal(item).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          "
                          readonly
                        />
                      </td>
                      <td class="margin-cell" v-if="show_cost_component">
                        <input
                          type="text"
                          class="input-number input-number-margin font-weight-bold blue-lighten-6"
                          :value="
                            getMargin(
                              getComponentPriceTotal(item),
                              getComponentCostTotal(item)
                            )
                          "
                          readonly
                        />
                      </td>
                    </tr>
                  </template>
                </tbody>

                <!-- Total shipment charges -->
                <tbody class="line-item-total">
                  <tr>
                    <td class="total-description"></td>
                    <td class="total-description">
                      <span class="font-weight-bold body-1">Total shipment price</span>
                    </td>
                    <td class="total-description"></td>
                    <!-- Quantity input -->
                    <td class="input-cell" colspan="3"></td>
                    <td class="input-cell">
                      <input
                        class="input-number font-weight-bold body-2"
                        :placeholder="
                          price_total.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        :value="
                          price_total.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        readonly
                      />
                    </td>
                    <td class="input-cell" v-if="show_cost_component" colspan="2"></td>
                    <td class="input-cell" v-if="show_cost_component">
                      <input
                        class="input-number font-weight-bold body-2"
                        :placeholder="
                          cost_total.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        :value="
                          cost_total.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        "
                        readonly
                      />
                    </td>
                    <td class="margin-cell" v-if="show_cost_component">
                      <input
                        type="text"
                        class="input-number input-number-margin font-weight-bold body-2"
                        :value="getMargin(price_total, cost_total)"
                        readonly
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </v-col>
          </v-row>

          <v-row no-gutters>
            <v-col class="financial-table-notes">
              <v-row class="py-1">
                <span class="caption">
                  B. PPU: Base price per unit; price per unit of line item in the original
                  quoted currency
                </span>
              </v-row>

              <v-row class="py-1">
                <span class="caption">
                  C. PPU: Converted price per unit; price per unit of line item after
                  conversion
                </span>
              </v-row>
            </v-col>
          </v-row>

          <v-row>
            <div class="af-checkbox" v-if="$root.is_admin && showCost">
              <input
                type="checkbox"
                class
                id="override_has_complete_price"
                v-model="quotation.override_has_complete_price"
              />
              <label class for="override_has_complete_price"
                >Override has complete price</label
              >
            </div>
          </v-row>
        </v-card>
      </v-col>

      <v-col v-else>
        <v-alert tile style="z-index: 1" type="error" class="my-10"
          >An error occured while trying generate a quotation financials.</v-alert
        >
      </v-col>
    </v-row>
  </v-row>
</template>

<script>
import constant from "../../const";
import DialogTonnageSelection from "@/components/admin/dialogs/DialogTonnageSelection.vue";
export default {
  props: ["value", "showCost"],
  components: {
    DialogTonnageSelection,
  },
  data() {
    return {
      quotation: this.value || null,
      CONSTANT: constant,
      snackbarColor: "",
      showSnackbar: false,
      snackbarMsg: "",
      snackbarTimeout: 6000,

      dialogOnConvertedCurrencies: false,
      dialogTonnageSelection: false,

      actualTonnageRate: null,
      tonnageRates: null,
      transaction_type: null,

      selectedFCItem: null,

      SingleLinesComponents: [
        constant.TYPE_OTHER_SERVICES_CHARGES,
        constant.TYPE_STORAGE_CHARGES,
        constant.TYPE_REMOVAL_CHARGES,
        constant.TYPE_HANDLING_CHARGES,
        constant.TYPE_DISCOUNT_REBATE,
      ],

      showExportCargoChargesTypeItems: true,
      showImportCargoChargesTypeItems: true,
      showOceanFreightChargesItems: true,
      showAirFreightChargesItems: true,
      showExportLocalChargesItems: true,
      showExportCustomsClearanceItems: true,
      showInsuranceItems: true,
      showImportCustomsClearanceItems: true,
      showImportLocalChargesItems: true,

      showExportHaulageItems: true,
      showImportHaulageItems: true,

      showExportTransportItems: true,
      showImportTransportItems: true,

      showOtherServices: true,

      convertedEditable: false,

      convertedCurrenciesList: [],
    };
  },
  created() {
    // load converted currency list
    if (this.$root.is_admin) {
      this.loadConvertedCurrenciesList();
    }
  },

  watch: {
    value: function () {
      this.quotation = this.value;
      // console.log("quotation: ", this.quotation)
    },
  },

  methods: {
    getQuantityColumnWidth() {
      return this.show_cost_component ? "width: 6%" : "width: 8%";
    },

    getNumericalColumnWidth() {
      return this.show_cost_component ? "width: 8%" : "width: 13%";
    },

    handleInput(e) {
      if (this.show_cost_component) {
        this.$emit("input", this.quotation);
      }
    },

    handleBaseRateInput(financial_component, conversion_factor) {
      if (this.show_cost_component) {
        financial_component.converted = parseFloat(
          (financial_component.base * conversion_factor).toFixed(2)
        );
        this.$emit("input", this.quotation);
      }
    },

    showSingleLineItem(showItem, financial_component) {
      return (
        financial_component != null &&
        (this.show_cost_component || financial_component.quantity > 0) &&
        showItem
      );
    },

    showSubLineItem(expand, financial_component) {
      if (!expand) {
        return false;
      }

      if (!this.show_cost_component && financial_component.quantity == 0) {
        return false;
      }

      return true;
    },

    showGroupItem(financial_component) {
      if (financial_component == null) {
        return false;
      }

      if (financial_component.length == 0) {
        return false;
      }

      for (var i = 0; i < financial_component.length; i++) {
        if (financial_component[i].quantity > 0) {
          return true;
        }
      }

      return false;
    },

    /** calculate component price total */
    getComponentPriceTotal(item) {
      return item.price.converted * item.quantity;
    },

    /** calculate component cost total */
    getComponentCostTotal(item) {
      return item.cost.converted * item.quantity;
    },

    /**
     * calculate component price
     */
    getComponentPrice(type) {
      let total = 0.0;
      //  console.log("component type: ", type)
      let component = this.fc[type];
      // console.log("component: ", component)
      component.forEach((item) => {
        total += item.price.converted * item.quantity;
      });
      return total;
    },

    /**
     * calculate component cost
     */
    getComponentCost(type) {
      let total = 0.0;
      // console.log("component type: ", type)
      let component = this.fc[type];
      // console.log("component: ", component)
      component.forEach((item) => {
        total += item.cost.converted * item.quantity;
      });
      return total;
    },

    getMargin(price, cost) {
      if (price <= 0 || cost <= 0) {
        return "0 %";
      }

      return (((price - cost) / price) * 100).toFixed(2) + " %";
    },

    getTotalRate(quantity, rate) {
      return parseFloat((quantity * rate).toFixed(2));
    },

    /**
     * Get the currency color
     */
    getCurrencyColor(item) {
      var color = "black";
      switch (item.currency) {
        case "MYR":
          color = "primary";
          break;
        case "USD":
          color = "green";
          break;
        case "SGD":
          color = "red";
          break;
        case "HKD":
          color = "orange";
          break;
        case "THB":
          color = "purple";
          break;
        case "EUR":
          color = "cyan darken-2";
          break;
        case "AUD":
          color = "blue-grey";
          break;
        case "CNY":
          color = "pink";
          break;
        case "JPY":
          color = "indigo";
          break;

        default:
          break;
      }
      return { color: color };
    },

    /**
     * Get the converted currency list
     */
    loadConvertedCurrenciesList() {
      this.$root.blockUImsg = "please wait...";
      this.$root.blockUIshow = true;
      this.$https
        .get(`/api/currencies/${this.quotation.currency}/converted`)
        .then((resp) => {
          if (resp.data.status == constant.RESPONSE_STATUS_OK) {
            this.convertedCurrenciesList = resp.data.data;
          } else {
            this.convertedCurrenciesList = [];
            this.showSnackbar = true;
            this.snackbarColor = "error";
            this.snackbarMsg = resp.data.msg;
          }
          this.$root.blockUIshow = false;
        })
        .catch((err) => {
          console.log(err);
          this.$root.blockUIshow = false;
          this.showSnackbar = true;
          this.snackbarColor = "error";
          this.snackbarMsg = err;
        });
    },

    /**
     * on open dialog for converted currency
     */
    onOpenConvertedcurrenciesDialog(fc_item) {
      // only admin can operate this function
      if (this.$root.is_admin) {
        // clone selected fc item
        this.selectedFCItem = fc_item;
        this.dialogOnConvertedCurrencies = true;
      }
    },

    /**
     * Update the converted currency rate to new rate
     */
    updateFCItemConverted(item) {
      this.selectedFCItem.currency = item.base_currency;
      this.selectedFCItem.conversion_factor = item.conversion_rate;

      // update the price
      this.selectedFCItem.price.converted = parseFloat(
        (this.selectedFCItem.price.base * this.selectedFCItem.conversion_factor).toFixed(
          2
        )
      );

      try {
        // cost, but only for admin
        if (this.show_cost_component) {
          this.selectedFCItem.cost.converted = parseFloat(
            (
              this.selectedFCItem.cost.base * this.selectedFCItem.conversion_factor
            ).toFixed(2)
          );
        }
      } catch (error) {
        console.log(error);
      }

      // close dialog
      this.dialogOnConvertedCurrencies = false;
    },

    getCurrencyUnitPriceText(financial_component) {
      if (financial_component == null) {
        return "(FC Error)";
      }

      var string = "";
      if (financial_component.currency != null) {
        // if min is used as base
        if (
          financial_component.price.min > 0 &&
          financial_component.price.min == financial_component.price.base
        ) {
          string =
            "(" + financial_component.currency + " " + financial_component.price.per_unit;

          switch (financial_component.uom) {
            case "WM":
              string = string + " per WM";
              break;
            case "RT":
              string = string + " per RT";
              break;
            case "CW":
              string = string + " per CW";
              break;
            case "CTR":
              string = string + " per container";
              break;
            case "SET":
              string = string + " per shipment";
              break;
            case "C3KG":
              string = string + " per 300KG, 1CBM:300KG";
              break;
            case "QTL":
              string = string + " per 100KG";
              break;
            default:
              string = string + ")";
          }

          string =
            string +
            ". Min. " +
            financial_component.currency +
            " " +
            financial_component.price.min +
            ")";
        } else {
          string =
            "(" + financial_component.currency + " " + financial_component.price.base;

          switch (financial_component.uom) {
            case "WM":
              string = string + " per WM)";
              break;
            case "RT":
              string = string + " per RT)";
              break;
            case "CW":
              string = string + " per CW)";
              break;
            case "CTR":
              string = string + " per container)";
              break;
            case "SET":
              string = string + " per shipment)";
              break;
            case "C3KG":
              string = string + " per 300KG, 1CBM:300KG)";
              break;
            case "QTL":
              string = string + " per 100KG)";
              break;
            default:
              string = string + ")";
          }
        }
      }

      return string;
    },

    /**
     * on tonnage selection
     */
    onTonnageSelection(tonnageRate, transactionType) {
      this.actualTonnageRate = tonnageRate;
      this.transaction_type = transactionType;

      if (this.transaction_type == "EXPORT") {
        // substitute the actual rate with its tonnage size
        for (let i = 0; i < this.quotation.export_transport_tonnage_rates.length; i++) {
          const tonnage = this.quotation.export_transport_tonnage_rates[i];
          if (tonnage.tonnage_size == tonnageRate.tonnage_size) {
            this.quotation.export_transport_tonnage_rates[i] = tonnageRate;
            break;
          }
        }

        this.tonnageRates = this.quotation.export_transport_tonnage_rates || [];
      } else {
        // substitute the actual rate with its tonnage size
        for (let i = 0; i < this.quotation.import_transport_tonnage_rates.length; i++) {
          const tonnage = this.quotation.import_transport_tonnage_rates[i];
          if (tonnage.tonnage_size == tonnageRate.tonnage_size) {
            this.quotation.import_transport_tonnage_rates[i] = tonnageRate;
            break;
          }
        }

        this.tonnageRates = this.quotation.import_transport_tonnage_rates || [];
      }

      this.dialogTonnageSelection = true;
    },

    /**
     * tonnage rate selected
     */
    tonnageSelected(component) {
      // if tonnage is not same
      if (this.actualTonnageRate.tonnage_size != component.tonnage_size) {
        // remove the actual tonnage rate
        this.removeComponent(this.actualTonnageRate);

        // replace with the selected tonnage rate
        this.quotation.estimate_component.push(component);
      }

      // replace the actual tonnage rate
      this.actualTonnageRate = component;
    },

    /**
     * On close tonnage selection dialog
     */
    onDialogClose() {
      this.dialogTonnageSelection = false;
      this.actualTonnageRate = null;
      this.transaction_type = null;
      this.tonnageRates = null;
    },

    /**
     * Add component to FC
     */
    addComponent(type, code, uom) {
      this.quotation.estimate_component.push({
        type: type,
        code: code,
        conversion_factor: 1.0,
        cost: {
          base: 0.0,
          min: 0.0,
          per_unit: 0.0,
          converted: 0.0,
        },
        currency: this.quotation.currency,
        description: null,
        price: {
          base: 0.0,
          min: 0.0,
          per_unit: 0.0,
          converted: 0.0,
        },
        quantity: 1,
        uom: uom,
      });
    },

    /**
     * Remove component from FC
     */
    removeComponent(item) {
      this.quotation.estimate_component = this.quotation.estimate_component.filter(
        function (component) {
          return component != item;
        }
      );
    },
  },
  computed: {
    // Show the cost components
    show_cost_component: {
      get() {
        return this.showCost && this.$root.is_admin;
      },
    },

    // Abbreviation for financial component
    fc: {
      get() {
        if (this.quotation == null) {
          return null;
        }

        let ret = {};
        // group the fc components
        this.quotation.estimate_component.forEach((component) => {
          if (ret.hasOwnProperty(component.type)) {
            ret[component.type].push(component);
          } else {
            ret[component.type] = [component];
          }
        });

        // console.log("fc: ", ret);

        return ret;
      },
    },

    // Show Ocean Freight
    show_ocean_freight_charges: {
      get() {
        return (
          this.quotation.transaction_type ==
            this.quotation.incoterm.responsibilities.freight &&
          this.quotation.freight_type == constant.QUOTATION_TYPE_SEA
        );
      },
    },

    // Show Air Freight
    show_air_freight_charges: {
      get() {
        return (
          this.quotation.transaction_type ==
            this.quotation.incoterm.responsibilities.freight &&
          this.quotation.freight_type == constant.QUOTATION_TYPE_AIR
        );
      },
    },

    // Show Insurance Charges
    show_insurance_charges: {
      get() {
        return (
          this.quotation.transaction_type ==
            this.quotation.incoterm.responsibilities.insurance &&
          this.quotation.include_insurance
        );
      },
    },

    // show other services charges
    show_other_services_charges: {
      get() {
        return this.quotation.include_other_services;
      },
    },

    // Show Export Customs charges
    show_export_customs_charges: {
      get() {
        return (
          this.quotation.transaction_type ==
            this.quotation.incoterm.responsibilities.export_clearance &&
          this.quotation.include_customs_clearance_export
        );
      },
    },

    // Show Import Customs charges
    show_import_customs_charges: {
      get() {
        return (
          this.quotation.transaction_type ==
            this.quotation.incoterm.responsibilities.import_clearance &&
          this.quotation.include_customs_clearance_import
        );
      },
    },

    // Show Export Local charges
    show_export_local_charges: {
      get() {
        return (
          this.quotation.transaction_type ==
          this.quotation.incoterm.responsibilities.export_local_charges
        );
      },
    },

    // Show Import Local charges
    show_import_local_charges: {
      get() {
        return (
          this.quotation.transaction_type ==
          this.quotation.incoterm.responsibilities.import_local_charges
        );
      },
    },

    // Show Export DG Class charges
    show_export_dgclass_charges: {
      get() {
        return (
          this.quotation.transaction_type ==
          this.quotation.incoterm.responsibilities.export_local_charges
        );
      },
    },

    // Show Import DG Class charges
    show_import_dgclass_charges: {
      get() {
        return (
          this.quotation.transaction_type ==
          this.quotation.incoterm.responsibilities.import_local_charges
        );
      },
    },

    // Show Export Haulage Charges
    show_export_haulage_charges: {
      get() {
        return (
          this.quotation.transaction_type ==
            this.quotation.incoterm.responsibilities.export_haulage_transport &&
          this.quotation.quotation_type == constant.QUOTATION_TYPE_SEA_FCL &&
          this.quotation.include_haulage_export
        );
      },
    },

    // Show Import Haulage Charges
    show_import_haulage_charges: {
      get() {
        return (
          this.quotation.transaction_type ==
            this.quotation.incoterm.responsibilities.import_haulage_transport &&
          this.quotation.quotation_type == constant.QUOTATION_TYPE_SEA_FCL &&
          this.quotation.include_haulage_import
        );
      },
    },

    // Show Export Transport Charges
    show_export_transport_charges: {
      get() {
        return (
          this.quotation.transaction_type ==
            this.quotation.incoterm.responsibilities.export_haulage_transport &&
          (this.quotation.quotation_type == constant.QUOTATION_TYPE_SEA_LCL ||
            this.quotation.quotation_type == constant.QUOTATION_TYPE_AIR) &&
          this.quotation.include_transport_export
        );
      },
    },

    // Show Import Transport Charges
    show_import_transport_charges: {
      get() {
        return (
          this.quotation.transaction_type ==
            this.quotation.incoterm.responsibilities.import_haulage_transport &&
          (this.quotation.quotation_type == constant.QUOTATION_TYPE_SEA_LCL ||
            this.quotation.quotation_type == constant.QUOTATION_TYPE_AIR) &&
          this.quotation.include_transport_import
        );
      },
    },

    //* Each Component Price & Cost Calculation
    // Get total price & cost ocean freight
    total_price_cost_ocean_freight_charges: {
      get() {
        let price = 0.0;
        let cost = 0.0;
        let total_qty = 0;

        // if valid & exist
        if (
          this.fc.hasOwnProperty(constant.TYPE_OCEAN_FREIGHT_CHARGES) &&
          this.show_ocean_freight_charges
        ) {
          let component = this.fc[constant.TYPE_OCEAN_FREIGHT_CHARGES];
          component.forEach((item) => {
            price += item.price.converted * item.quantity;
            cost += item.cost.converted * item.quantity;
            total_qty += item.quantity;
          });
        }

        return {
          price: price,
          cost: cost,
          total_qty: total_qty,
        };
      },
    },

    // Get total price & cost air freight
    total_price_cost_air_freight_charges: {
      get() {
        let price = 0.0;
        let cost = 0.0;
        let total_qty = 0;

        // if valid & exist
        if (
          this.fc.hasOwnProperty(constant.TYPE_AIR_FREIGHT_CHARGES) &&
          this.show_air_freight_charges
        ) {
          let component = this.fc[constant.TYPE_AIR_FREIGHT_CHARGES];
          component.forEach((item) => {
            price += item.price.converted * item.quantity;
            cost += item.cost.converted * item.quantity;
            total_qty += item.quantity;
          });
        }

        return {
          price: price,
          cost: cost,
          total_qty: total_qty,
        };
      },
    },

    // Get total price & cost export customs charges
    total_price_cost_export_customs_charges: {
      get() {
        let price = 0.0;
        let cost = 0.0;
        let total_qty = 0;

        // if valid & exist
        if (
          this.fc.hasOwnProperty(constant.TYPE_EXPORT_CUSTOMS_CHARGES) &&
          this.show_export_customs_charges
        ) {
          let component = this.fc[constant.TYPE_EXPORT_CUSTOMS_CHARGES];
          component.forEach((item) => {
            price += item.price.converted * item.quantity;
            cost += item.cost.converted * item.quantity;
            total_qty += item.quantity;
          });
        }

        return {
          price: price,
          cost: cost,
          total_qty: total_qty,
        };
      },
    },

    // Get total cost import customs charges
    total_price_cost_import_customs_charges: {
      get() {
        let price = 0.0;
        let cost = 0.0;
        let total_qty = 0;

        // if valid & exist
        if (
          this.fc.hasOwnProperty(constant.TYPE_IMPORT_CUSTOMS_CHARGES) &&
          this.show_import_customs_charges
        ) {
          let component = this.fc[constant.TYPE_IMPORT_CUSTOMS_CHARGES];
          component.forEach((item) => {
            price += item.price.converted * item.quantity;
            cost += item.cost.converted * item.quantity;
            total_qty += item.quantity;
          });
        }

        return {
          price: price,
          cost: cost,
          total_qty: total_qty,
        };
      },
    },

    // Get total price & cost export local charges
    total_price_cost_export_local_charges: {
      get() {
        let price = 0.0;
        let cost = 0.0;
        let total_qty = 0;

        // if valid & exist
        if (this.fc.hasOwnProperty(constant.TYPE_EXPORT_LOCAL_CHARGES)) {
          let component = this.fc[constant.TYPE_EXPORT_LOCAL_CHARGES];
          component.forEach((item) => {
            price += item.price.converted * item.quantity;
            cost += item.cost.converted * item.quantity;
            total_qty += item.quantity;
          });
        }

        return {
          price: price,
          cost: cost,
          total_qty: total_qty,
        };
      },
    },

    // Get total price & cost import local charges
    total_price_cost_import_local_charges: {
      get() {
        let price = 0.0;
        let cost = 0.0;
        let total_qty = 0;

        // if valid & exist
        if (this.fc.hasOwnProperty(constant.TYPE_IMPORT_LOCAL_CHARGES)) {
          let component = this.fc[constant.TYPE_IMPORT_LOCAL_CHARGES];
          component.forEach((item) => {
            price += item.price.converted * item.quantity;
            cost += item.cost.converted * item.quantity;
            total_qty += item.quantity;
          });
        }

        return {
          price: price,
          cost: cost,
          total_qty: total_qty,
        };
      },
    },

    // Get total price & cost export dgclass charges
    total_price_cost_export_dgclass_charges: {
      get() {
        let price = 0.0;
        let cost = 0.0;
        let total_qty = 0;

        // if valid & exist
        if (
          this.fc.hasOwnProperty(constant.TYPE_EXPORT_DGCLASS_CHARGES) &&
          this.show_export_dgclass_charges
        ) {
          let component = this.fc[constant.TYPE_EXPORT_DGCLASS_CHARGES];
          component.forEach((item) => {
            price += item.price.converted * item.quantity;
            cost += item.cost.converted * item.quantity;
            total_qty += item.quantity;
          });
        }

        return {
          price: price,
          cost: cost,
          total_qty: total_qty,
        };
      },
    },

    // Get total price & cost import dgclass charges
    total_price_cost_import_dgclass_charges: {
      get() {
        let price = 0.0;
        let cost = 0.0;
        let total_qty = 0;

        // if valid & exist
        if (
          this.fc.hasOwnProperty(constant.TYPE_IMPORT_DGCLASS_CHARGES) &&
          this.show_import_dgclass_charges
        ) {
          let component = this.fc[constant.TYPE_IMPORT_DGCLASS_CHARGES];
          component.forEach((item) => {
            price += item.price.converted * item.quantity;
            cost += item.cost.converted * item.quantity;
            total_qty += item.quantity;
          });
        }

        return {
          price: price,
          cost: cost,
          total_qty: total_qty,
        };
      },
    },

    // Get total price & cost export haulage charges
    total_price_cost_export_haulage_charges: {
      get() {
        let price = 0.0;
        let cost = 0.0;
        let total_qty = 0;

        // if valid & exist
        if (
          this.fc.hasOwnProperty(constant.TYPE_EXPORT_HAULAGE_CHARGES) &&
          this.show_export_haulage_charges
        ) {
          let component = this.fc[constant.TYPE_EXPORT_HAULAGE_CHARGES];
          component.forEach((item) => {
            price += item.price.converted * item.quantity;
            cost += item.cost.converted * item.quantity;
            total_qty += item.quantity;
          });
        }

        return {
          price: price,
          cost: cost,
          total_qty: total_qty,
        };
      },
    },

    // Get total price & cost import haulage charges
    total_price_cost_import_haulage_charges: {
      get() {
        let price = 0.0;
        let cost = 0.0;
        let total_qty = 0;

        // if valid & exist
        if (
          this.fc.hasOwnProperty(constant.TYPE_IMPORT_HAULAGE_CHARGES) &&
          this.show_import_haulage_charges
        ) {
          let component = this.fc[constant.TYPE_IMPORT_HAULAGE_CHARGES];
          component.forEach((item) => {
            price += item.price.converted * item.quantity;
            cost += item.cost.converted * item.quantity;
            total_qty += item.quantity;
          });
        }

        return {
          price: price,
          cost: cost,
          total_qty: total_qty,
        };
      },
    },

    // Get total price & cost export transport charges
    total_price_cost_export_transport_charges: {
      get() {
        let price = 0.0;
        let cost = 0.0;
        let total_qty = 0;

        // if valid & exist
        if (
          this.fc.hasOwnProperty(constant.TYPE_EXPORT_TRANSPORT_CHARGES) &&
          this.show_export_transport_charges
        ) {
          let component = this.fc[constant.TYPE_EXPORT_TRANSPORT_CHARGES];
          component.forEach((item) => {
            price += item.price.converted * item.quantity;
            cost += item.cost.converted * item.quantity;
            total_qty += item.quantity;
          });
        }

        return {
          price: price,
          cost: cost,
          total_qty: total_qty,
        };
      },
    },

    // Get total price & cost import transport charges
    total_price_cost_import_transport_charges: {
      get() {
        let price = 0.0;
        let cost = 0.0;
        let total_qty = 0;

        // if valid & exist
        if (
          this.fc.hasOwnProperty(constant.TYPE_IMPORT_TRANSPORT_CHARGES) &&
          this.show_import_transport_charges
        ) {
          let component = this.fc[constant.TYPE_IMPORT_TRANSPORT_CHARGES];
          component.forEach((item) => {
            price += item.price.converted * item.quantity;
            cost += item.cost.converted * item.quantity;
            total_qty += item.quantity;
          });
        }

        return {
          price: price,
          cost: cost,
          total_qty: total_qty,
        };
      },
    },

    // Get total price & cost storage charges
    total_price_cost_storage_charges: {
      get() {
        let price = 0.0;
        let cost = 0.0;
        let total_qty = 0;

        // if valid & exist
        if (this.fc.hasOwnProperty(constant.TYPE_STORAGE_CHARGES)) {
          let component = this.fc[constant.TYPE_STORAGE_CHARGES];
          component.forEach((item) => {
            price += item.price.converted * item.quantity;
            cost += item.cost.converted * item.quantity;
            total_qty += item.quantity;
          });
        }

        return {
          price: price,
          cost: cost,
          total_qty: total_qty,
        };
      },
    },

    // Get total price & cost removal charges
    total_price_cost_removal_charges: {
      get() {
        let price = 0.0;
        let cost = 0.0;
        let total_qty = 0;

        // if valid & exist
        if (this.fc.hasOwnProperty(constant.TYPE_REMOVAL_CHARGES)) {
          let component = this.fc[constant.TYPE_REMOVAL_CHARGES];
          component.forEach((item) => {
            price += item.price.converted * item.quantity;
            cost += item.cost.converted * item.quantity;
            total_qty += item.quantity;
          });
        }

        return {
          price: price,
          cost: cost,
          total_qty: total_qty,
        };
      },
    },

    // Get total price & cost insurance charges
    total_price_cost_insurance_charges: {
      get() {
        let price = 0.0;
        let cost = 0.0;
        let total_qty = 0;

        // if valid & exist
        if (
          this.fc.hasOwnProperty(constant.TYPE_INSURANCE_CHARGES) &&
          this.show_insurance_charges
        ) {
          let component = this.fc[constant.TYPE_INSURANCE_CHARGES];
          component.forEach((item) => {
            price += item.price.converted * item.quantity;
            cost += item.cost.converted * item.quantity;
            total_qty += item.quantity;
          });
        }

        return {
          price: price,
          cost: cost,
          total_qty: total_qty,
        };
      },
    },

    // Get total price & cost other services charges
    total_price_cost_other_services_charges: {
      get() {
        let price = 0.0;
        let cost = 0.0;
        let total_qty = 0;

        // if valid & exist
        if (
          this.fc.hasOwnProperty(constant.TYPE_OTHER_SERVICES_CHARGES) &&
          this.show_other_services_charges
        ) {
          let component = this.fc[constant.TYPE_OTHER_SERVICES_CHARGES];
          component.forEach((item) => {
            price += item.price.converted * item.quantity;
            cost += item.cost.converted * item.quantity;
            total_qty += item.quantity;
          });
        }

        return {
          price: price,
          cost: cost,
          total_qty: total_qty,
        };
      },
    },

    // Get total price & cost handling charges
    total_price_cost_handling_charges: {
      get() {
        let price = 0.0;
        let cost = 0.0;
        let total_qty = 0;

        // if valid & exist
        if (this.fc.hasOwnProperty(constant.TYPE_HANDLING_CHARGES)) {
          let component = this.fc[constant.TYPE_HANDLING_CHARGES];
          component.forEach((item) => {
            price += item.price.converted * item.quantity;
            cost += item.cost.converted * item.quantity;
            total_qty += item.quantity;
          });
        }

        return {
          price: price,
          cost: cost,
          total_qty: total_qty,
        };
      },
    },

    // Get total price & cost general items
    total_price_cost_general_items: {
      get() {
        let price = 0.0;
        let cost = 0.0;
        let total_qty = 0;

        // if valid & exist
        if (this.fc.hasOwnProperty(constant.TYPE_GENERAL_ITEM)) {
          let component = this.fc[constant.TYPE_GENERAL_ITEM];
          component.forEach((item) => {
            price += item.price.converted * item.quantity;
            cost += item.cost.converted * item.quantity;
            total_qty += item.quantity;
          });
        }

        return {
          price: price,
          cost: cost,
          total_qty: total_qty,
        };
      },
    },

    // Get total price & cost discount rebate
    total_price_cost_discount_rebate: {
      get() {
        let price = 0.0;
        let cost = 0.0;
        let total_qty = 0;

        // if valid & exist
        if (this.fc.hasOwnProperty(constant.TYPE_DISCOUNT_REBATE)) {
          let component = this.fc[constant.TYPE_DISCOUNT_REBATE];
          component.forEach((item) => {
            price += item.price.converted * item.quantity;
            cost += item.cost.converted * item.quantity;
            total_qty += item.quantity;
          });
        }

        return {
          price: price,
          cost: cost,
          total_qty: total_qty,
        };
      },
    },

    // Get total price
    price_total: {
      get() {
        let totalPrice = 0.0;

        // this.quotation.estimate_component.forEach((component) => {
        //   totalPrice += component.price.converted * component.quantity
        // })

        totalPrice =
          this.total_price_cost_ocean_freight_charges.price +
          this.total_price_cost_air_freight_charges.price +
          this.total_price_cost_export_customs_charges.price +
          this.total_price_cost_import_customs_charges.price +
          this.total_price_cost_export_local_charges.price +
          this.total_price_cost_import_local_charges.price +
          this.total_price_cost_export_dgclass_charges.price +
          this.total_price_cost_import_dgclass_charges.price +
          this.total_price_cost_export_haulage_charges.price +
          this.total_price_cost_import_haulage_charges.price +
          this.total_price_cost_export_transport_charges.price +
          this.total_price_cost_import_transport_charges.price +
          this.total_price_cost_insurance_charges.price +
          this.total_price_cost_other_services_charges.price +
          this.total_price_cost_storage_charges.price +
          this.total_price_cost_removal_charges.price +
          this.total_price_cost_handling_charges.price +
          this.total_price_cost_general_items.price +
          this.total_price_cost_discount_rebate.price;

        return parseFloat(totalPrice.toFixed(2));
      },
    },

    cost_total: {
      get() {
        var totalCost = 0.0;

        // this.quotation.estimate_component.forEach((component) => {
        //   totalCost += component.cost.converted * component.quantity
        // })

        totalCost =
          this.total_price_cost_ocean_freight_charges.cost +
          this.total_price_cost_air_freight_charges.cost +
          this.total_price_cost_export_customs_charges.cost +
          this.total_price_cost_import_customs_charges.cost +
          this.total_price_cost_export_local_charges.cost +
          this.total_price_cost_import_local_charges.cost +
          this.total_price_cost_export_dgclass_charges.cost +
          this.total_price_cost_import_dgclass_charges.cost +
          this.total_price_cost_export_haulage_charges.cost +
          this.total_price_cost_import_haulage_charges.cost +
          this.total_price_cost_export_transport_charges.cost +
          this.total_price_cost_import_transport_charges.cost +
          this.total_price_cost_insurance_charges.cost +
          this.total_price_cost_other_services_charges.cost +
          this.total_price_cost_storage_charges.cost +
          this.total_price_cost_removal_charges.cost +
          this.total_price_cost_handling_charges.cost +
          this.total_price_cost_general_items.cost +
          this.total_price_cost_discount_rebate.cost;

        return parseFloat(totalCost.toFixed(2));
      },
    },

    has_complete_price: {
      get() {
        if (this.quotation == null) {
          return false;
        }

        if (
          this.quotation.include_customs_clearance_export &&
          !this.quotation.origin_port.has_export_customs_charges
        ) {
          console.log("Export clearance invalid");
          return false;
        }

        if (
          this.quotation.include_customs_clearance_import &&
          !this.quotation.destination_port.has_import_customs_charges
        ) {
          console.log(
            "Import clearance invalid",
            this.quotation.destination_port.has_import_customs_charges
          );
          return false;
        }

        if (this.quotation.require_local_charges_export) {
          if (
            this.quotation.quotation_type == constant.QUOTATION_TYPE_SEA_FCL &&
            !this.quotation.origin_port.has_export_local_charges_fcl
          ) {
            console.log("Export local charges FCL invalid");
            return false;
          } else if (
            this.quotation.quotation_type == constant.QUOTATION_TYPE_SEA_LCL &&
            !this.quotation.origin_port.has_export_local_charges_lcl
          ) {
            console.log("Export local charges LCL invalid");
            return false;
          } else if (
            this.quotation.quotation_type == constant.QUOTATION_TYPE_AIR &&
            !this.quotation.origin_port.has_export_local_charges_air
          ) {
            console.log("Export local charges AIR invalid");
            return false;
          }
        }

        if (this.quotation.require_local_charges_import) {
          if (
            this.quotation.quotation_type == constant.QUOTATION_TYPE_SEA_FCL &&
            !this.quotation.destination_port.has_import_local_charges_fcl
          ) {
            console.log("Import local charges FCL invalid");
            return false;
          } else if (
            this.quotation.quotation_type == constant.QUOTATION_TYPE_SEA_LCL &&
            !this.quotation.destination_port.has_import_local_charges_lcl
          ) {
            console.log("Import local charges LCL invalid");
            return false;
          } else if (
            this.quotation.quotation_type == constant.QUOTATION_TYPE_AIR &&
            !this.quotation.destination_port.has_import_local_charges_air
          ) {
            console.log("Import local charges AIR invalid");
            return false;
          }
        }

        if (
          this.quotation.include_haulage_export &&
          !this.quotation.has_haulage_export_rate
        ) {
          console.log("Haulage export invalid");
          return false;
        }

        if (
          this.quotation.include_haulage_import &&
          !this.quotation.has_haulage_import_rate
        ) {
          console.log("Haulage import invalid");
          return false;
        }

        if (
          this.quotation.include_transport_export &&
          !this.quotation.has_transport_export_rate
        ) {
          console.log("Transport export invalid");
          return false;
        }

        if (
          this.quotation.include_transport_import &&
          !this.quotation.has_transport_import_rate
        ) {
          console.log("Transport import invalid");
          return false;
        }

        if (this.quotation.freight_required && !this.quotation.has_freight_rate) {
          console.log("Freight rates invalid");
          return false;
        }

        return true;
      },
    },
  },
};
</script>

<style scoped>
.update-converted-currency:hover {
  background-color: #bcd0e4;
}

.blue-lighten-6 {
  background-color: #f4faff !important;
}

.v-application .blue-lighten-6 {
  background-color: #f4faff !important;
  border-color: #f4faff !important;
}

.single-description {
  border-bottom: 1px solid #e0e0e0;
}

.component-title-header {
  border-left: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
}

.v-btn--icon.v-size--x-small.add-remove-item {
  height: 14px;
  width: 14px;
}

/* .editable-description {
  max-width: 100%;
} */
</style>
